/**
 * NewsletterDashboard.js
 * Author: Markus.van.Kempen@gmail.com
 * Date: 2nd Nov 2025
 */

import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { getProductImage } from '../utils/imageMapping';
import AssetSearchModal from './AssetSearchModal';
import './NewsletterDashboard.css';

function normalizeNewsletterAuthorName(authorName) {
  if (!authorName) return 'Paul S. Cranford';

  const name = authorName.trim();
  const nameLower = name.toLowerCase();

  if (nameLower.includes('paul') && nameLower.includes('cranford')) {
    if (nameLower.includes('paul s') || nameLower.includes('paul s.') ||
      nameLower.includes('paul stuart') || nameLower.includes('paul s cranford') ||
      nameLower.includes('paul s. cranford') || nameLower.includes('paul stuart cranford')) {
      return 'Paul S. Cranford';
    }
    if (nameLower === 'paul cranford') {
      return 'Paul S. Cranford';
    }
  }

  return name;
}

function extractAuthorFromHtml(html) {
  if (!html) return 'Paul S. Cranford';

  const authorPatterns = [
    /Author:\s*([^<\n]+)/i,
    /By:\s*([^<\n]+)/i,
    /Written by:\s*([^<\n]+)/i,
    /<strong>Author:<\/strong>\s*([^<\n]+)/i,
    /<b>Author:<\/b>\s*([^<\n]+)/i
  ];

  for (const pattern of authorPatterns) {
    const match = html.match(pattern);
    if (match && match[1]) {
      const extractedAuthor = match[1].trim().replace(/<[^>]*>/g, '');
      return normalizeNewsletterAuthorName(extractedAuthor);
    }
  }

  return 'Paul S. Cranford';
}

function generateNewsletterTags(html, title, description, existingMetadata) {
  if (existingMetadata) {
    try {
      const parsed = typeof existingMetadata === 'string' ? JSON.parse(existingMetadata) : existingMetadata;
      if (parsed && parsed.tags) {
        const tagsFromMetadata = typeof parsed.tags === 'string'
          ? parsed.tags.split(',').map(t => t.trim()).filter(t => t)
          : Array.isArray(parsed.tags) ? parsed.tags.map(t => typeof t === 'string' ? t.trim() : String(t)).filter(t => t) : [];

        if (tagsFromMetadata.length > 0) {
          return tagsFromMetadata;
        }
      }
    } catch (e) {
      console.error('Error parsing metadata for tags:', e, existingMetadata);
    }
  }

  if (!html) return ['Article'];

  const tags = new Set();
  const text = html.replace(/<[^>]*>/g, ' ').toLowerCase();
  const allText = `${title} ${description} ${text}`.toLowerCase();

  const keywords = [
    'fiddle', 'fiddler', 'tune', 'reel', 'jig', 'strathspey', 'waltz',
    'cape breton', 'scottish', 'irish', 'celtic', 'traditional',
    'music', 'musician', 'recording', 'album', 'cd',
    'biography', 'history', 'heritage', 'culture',
    'dance', 'dancing', 'folk', 'folk music', 'memoir', 'story',
    'bagpipe', 'piano', 'guitar', 'violin', 'accordion',
    'highland', 'gaelic', 'nova scotia', 'canada', 'scotland',
    'concert', 'performance', 'festival', 'teaching', 'composer',
    'review', 'reviewed', 'critique', 'recommendation', 'recommended'
  ];

  keywords.forEach(keyword => {
    if (allText.includes(keyword)) {
      tags.add(keyword.split(' ').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' '));
    }
  });

  if (tags.size === 0) {
    tags.add('Article');
    tags.add('Historical Content');
  }

  return Array.from(tags).slice(0, 10);
}

function buildDefaultNewsletterData() {
  const today = new Date();
  const dateString = today.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
  return {
    contentMode: 'manual',
    title: `Cranfordpub Newsletter - ${dateString}`,
    subject: `🎻 Cranfordpub Newsletter 🎻 - ${dateString}`,
    content: '',
    tags: [],
    scheduled: false,
    scheduledDate: '',
    linkedItems: [],
    templateId: '',
    templateVariables: {},
    htmlContent: '',
    textContent: '',
    recipientMode: 'all',
    selectedRecipients: [],
    recipientFilters: {
      newsletterSubscribersOnly: false,
      verifiedEmailsOnly: false,
      roleFilter: ''
    },
    backgroundImage: '',
    backgroundColor: 'var(--color-surface)',
    backgroundUrl: '',
    aiTemperature: 0.7,
    contentFormat: 'fragment',
    batchSize: 100,
    excludeAlreadySent: true
  };
}

function parseNewsletterLinkedItems(raw) {
  if (!raw) return [];
  if (Array.isArray(raw)) return raw;
  try {
    const parsed = typeof raw === 'string' ? JSON.parse(raw) : raw;
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

const NewsletterDashboard = () => {
  const { currentUser, hasPermission } = useAuth();
  const [activeTab, setActiveTab] = useState('overview');
  const [newsletterData, setNewsletterData] = useState(buildDefaultNewsletterData);
  const [editingCampaignId, setEditingCampaignId] = useState(null);
  const [blogPosts, setBlogPosts] = useState([]);
  const [products, setProducts] = useState([]);
  const [tunes, setTunes] = useState([]);
  const [customLink, setCustomLink] = useState({ title: '', url: '', description: '' });

  // Search states
  const [productSearchTerm, setProductSearchTerm] = useState('');
  const [articleSearchTerm, setArticleSearchTerm] = useState('');
  const [tuneSearchTerm, setTuneSearchTerm] = useState('');
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [filteredArticles, setFilteredArticles] = useState([]);
  const [filteredTunes, setFilteredTunes] = useState([]);

  // Recipient selection states
  const [recipients, setRecipients] = useState([]);
  const [recipientSearchTerm, setRecipientSearchTerm] = useState('');
  const [filteredRecipients, setFilteredRecipients] = useState([]);
  const [showAssetModal, setShowAssetModal] = useState(false);

  // Automated newsletter state
  const [automationConfig, setAutomationConfig] = useState({
    includeRecentArticles: true,
    includeNewProducts: true,
    includePromotions: false,
    articleLimit: 3,
    productLimit: 3,
    daysBack: 30,
    theme: 'celtic music',
    tone: 'friendly and engaging',
    scheduleDate: '',
    aiProvider: 'watsonx', // Default to Watsonx since we have it configured
    useSeasonalDesign: false,
    designTheme: 'standard'
  });
  const [automatedPreview, setAutomatedPreview] = useState(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [isPreviewing, setIsPreviewing] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [selectedCampaigns, setSelectedCampaigns] = useState([]);
  const [deleteConfirm, setDeleteConfirm] = useState(null); // {id, title} or null
  const [sendSuccessModal, setSendSuccessModal] = useState(null); // {visible, campaignId, recipientInfo}
  const [batchSendModal, setBatchSendModal] = useState(null); // { campaignId, title, batchSize }
  const [campaignRecipientsModal, setCampaignRecipientsModal] = useState(null); // send-status payload
  const [loadingCampaignRecipients, setLoadingCampaignRecipients] = useState(false);
  const [isCreatingFromTemplate, setIsCreatingFromTemplate] = useState(false);
  const [sendSafety, setSendSafety] = useState({
    allowed: true,
    mode: 'live',
    maxTestRecipients: 2,
    reason: '',
    baseUrl: ''
  });

  // ===== EMAIL TEMPLATE STATE (for Template mode) =====
  const [emailTemplates, setEmailTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);

  const isTestSendMode = sendSafety?.mode === 'test';
  const maxTestRecipients = sendSafety?.maxTestRecipients || 2;
  const massSendsBlocked = isTestSendMode; // batch / all-subscriber sends blocked locally
  const canSendTestSelection =
    !isTestSendMode || (
      newsletterData.recipientMode === 'selected' &&
      newsletterData.selectedRecipients.length >= 1 &&
      newsletterData.selectedRecipients.length <= maxTestRecipients
    );

  const loadCampaignRowIntoEditor = (campaign) => {
    setNewsletterData({
      ...buildDefaultNewsletterData(),
      contentMode: 'manual',
      title: campaign.name || '',
      subject: campaign.subject || '',
      content: campaign.content || '',
      tags: [],
      linkedItems: parseNewsletterLinkedItems(campaign.linked_items),
      templateId: '',
      templateVariables: {},
      htmlContent: '',
      textContent: '',
      recipientMode: 'all',
      selectedRecipients: [],
      recipientFilters: {
        newsletterSubscribersOnly: false,
        verifiedEmailsOnly: false,
        roleFilter: ''
      },
      backgroundImage: campaign.background_image || '',
      backgroundColor: campaign.background_color || 'var(--color-surface)',
      backgroundUrl: campaign.background_url || '',
      scheduled: false,
      scheduledDate: '',
      contentFormat: campaign.content_format === 'full_html' ? 'full_html' : 'fragment',
      batchSize: 100,
      excludeAlreadySent: true
    });
  };

  const handleEditCampaign = async (campaignId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`/api/newsletter/admin/campaigns/${campaignId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.error || 'Failed to load campaign');
      }
      const campaign = await response.json();
      loadCampaignRowIntoEditor(campaign);
      setEditingCampaignId(Number(campaignId));
      setActiveTab('create');
      setTimeout(() => window.scrollTo({ top: 0, behavior: 'smooth' }), 100);
    } catch (e) {
      console.error(e);
      alert('Failed to open campaign for editing: ' + e.message);
    }
  };

  const handleStartNewCampaign = () => {
    setEditingCampaignId(null);
    setNewsletterData(buildDefaultNewsletterData());
  };

  const handleLoadWebsiteAnnouncement = async () => {
    setIsCreatingFromTemplate(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch('/api/newsletter/admin/campaigns/from-built-in', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ templateKey: 'website-announcement' })
      });
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.error || 'Failed to create campaign from announcement template');
      }
      const data = await response.json();
      loadCampaignRowIntoEditor(data.campaign);
      setEditingCampaignId(Number(data.campaignId));
      setActiveTab('create');
      fetchDashboardStats();
      alert(`✅ Website Launch Announcement loaded as campaign #${data.campaignId}.\n\nChoose a batch size (100/200/500/1000) and send. Already-sent customers are skipped automatically.`);
    } catch (e) {
      console.error(e);
      alert('Failed to load announcement template: ' + e.message);
    } finally {
      setIsCreatingFromTemplate(false);
    }
  };

  const handleViewCampaignRecipients = async (campaignId) => {
    setLoadingCampaignRecipients(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`/api/newsletter/admin/campaigns/${campaignId}/send-status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.error || 'Failed to load send status');
      }
      const data = await response.json();
      setCampaignRecipientsModal(data);
    } catch (e) {
      console.error(e);
      alert('Failed to load recipients: ' + e.message);
    } finally {
      setLoadingCampaignRecipients(false);
    }
  };

  const handleSendCampaignBatch = async (campaignId, batchSize = 100) => {
    if (massSendsBlocked) {
      alert(`🚫 Mass/batch sends are blocked on localhost.\n\nFor testing, open the campaign, select 1–${maxTestRecipients} individual recipients, then click Send.`);
      return;
    }
    if (!window.confirm(`Send next batch of up to ${batchSize} emails for campaign #${campaignId}?\n\nCustomers who already received this campaign will be skipped.`)) {
      return;
    }
    setIsSending(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`/api/newsletter/admin/campaigns/${campaignId}/send`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          recipients: null,
          batchSize,
          excludeAlreadySent: true
        })
      });
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.error || 'Failed to start batch send');
      }
      const result = await response.json();
      setBatchSendModal(null);
      setSendSuccessModal({
        visible: true,
        campaignId,
        recipientInfo: `batch of ${result.planned || 0} (already sent: ${result.alreadySent || 0}, remaining before: ${result.remainingBefore || 0})`
      });
      fetchDashboardStats();
    } catch (e) {
      console.error(e);
      alert('Batch send failed: ' + e.message);
    } finally {
      setIsSending(false);
    }
  };

  // Fetch blog posts, products, and tunes
  React.useEffect(() => {
    const token = localStorage.getItem('token');

    // Load articles using consistent logic from Article.js
    const loadArticleContent = async () => {
      try {
        const response = await fetch('/api/content/articles', {
          headers: { 'Authorization': `Bearer ${token}` }
        });

        if (!response.ok) throw new Error(`Failed to load articles: ${response.status}`);

        const pages = await response.json();

        // Need to fetch full content for each page to extract author and tags
        const processedContentPromises = pages.map(async (page) => {
          try {
            const detailResponse = await fetch(`/api/content/pages/${page.page_id}`);
            let author = 'Paul S. Cranford';
            let detailData = null;

            if (detailResponse.ok) {
              detailData = await detailResponse.json();

              // Try to get author from metadata first, then extract from HTML
              if (detailData.metadata) {
                try {
                  const parsedMeta = typeof detailData.metadata === 'string' ? JSON.parse(detailData.metadata) : detailData.metadata;
                  if (parsedMeta.author) {
                    author = normalizeNewsletterAuthorName(parsedMeta.author);
                  } else {
                    author = extractAuthorFromHtml(detailData.html);
                  }
                } catch (e) {
                  console.error('Error parsing metadata for author:', e);
                  author = extractAuthorFromHtml(detailData.html);
                }
              } else {
                author = extractAuthorFromHtml(detailData.html);
              }
            }

            // Parse metadata
            let parsedMetadata = {};
            const metadataSource = detailData?.metadata || page.metadata;
            if (metadataSource) {
              try {
                parsedMetadata = typeof metadataSource === 'string' ? JSON.parse(metadataSource) : metadataSource;
              } catch (e) {
                // Ignore parse errors
              }
            }

            // Generate tags
            const tags = detailData
              ? generateNewsletterTags(detailData.html, page.title || '', page.description || '', metadataSource)
              : generateNewsletterTags('', page.title || '', page.description || '', metadataSource);

            return {
              id: page.page_id,
              page_id: page.page_id,
              title: page.title || page.page_id,
              excerpt: page.description,
              description: page.description || '',
              content: detailData?.html || '',
              slug: page.page_id,
              author: author,
              tags: tags,
              publish_date: page.created_at,
              metadata: parsedMetadata
            };
          } catch (err) {
            console.error(`Error fetching details for ${page.page_id}:`, err);
            // Return minimal data on error
            return {
              id: page.page_id,
              page_id: page.page_id,
              title: page.title || page.page_id,
              excerpt: page.description,
              description: page.description || '',
              content: '',
              slug: page.page_id,
              author: 'Paul S. Cranford',
              tags: [],
              publish_date: page.created_at,
              metadata: {}
            };
          }
        });

        const processedContent = await Promise.all(processedContentPromises);

        // Sort alphabetically by title
        processedContent.sort((a, b) => {
          const titleA = (a.title || '').toLowerCase();
          const titleB = (b.title || '').toLowerCase();
          return titleA.localeCompare(titleB);
        });

        setBlogPosts(processedContent);
        setFilteredArticles(processedContent);
      } catch (err) {
        console.error('Error fetching articles:', err);
      }
    };

    loadArticleContent();

    // Fetch products from API instead of JSON file
    fetch('/api/products', {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    })
      .then(res => res.json())
      .then(data => {
        const prods = Array.isArray(data) ? data : [];
        setProducts(prods);
        setFilteredProducts(prods);
      })
      .catch(err => {
        console.error('Error fetching products:', err);
        // Fallback to JSON file if API fails
        fetch(`${process.env.PUBLIC_URL}/products.json`)
          .then(res => res.json())
          .then(data => {
            setProducts(data || []);
            setFilteredProducts(data || []);
          });
      });

    // Fetch tunes
    fetch('/api/content/tunes', {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    })
      .then(res => res.json())
      .then(data => {
        const tunesList = Array.isArray(data) ? data : (data.tunes || []);
        setTunes(tunesList);
        setFilteredTunes(tunesList);
      })
      .catch(err => {
        console.error('Error fetching tunes:', err);
        // Try alternative endpoint
        fetch('/api/tunes', {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        })
          .then(res => res.json())
          .then(data => {
            const tunesList = Array.isArray(data) ? data : (data.tunes || []);
            setTunes(tunesList);
            setFilteredTunes(tunesList);
          })
          .catch(err2 => console.error('Error fetching tunes from alternative endpoint:', err2));
      });

    // Fetch recipients (subscribers + users)
    fetch('/api/newsletter/admin/recipients', {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    })
      .then(res => res.json())
      .then(data => {
        console.log('Recipients API response:', data);
        // Backend returns { recipients: [...], total: N }
        let recipientsList = [];
        if (Array.isArray(data)) {
          recipientsList = data;
        } else if (data && Array.isArray(data.recipients)) {
          recipientsList = data.recipients;
        } else if (data && typeof data === 'object') {
          console.warn('Unexpected recipients data format:', data);
          recipientsList = [];
        }

        // FILTER: Only show active newsletter subscribers
        // Exclude users who haven't subscribed to newsletters
        recipientsList = recipientsList.filter(recipient => {
          // For newsletter_subscribers table entries, check status is 'active'
          if (recipient.source === 'subscriber') {
            return recipient.status === 'active';
          }
          // For users, they're already filtered by newsletter_subscribed=1 on backend
          // but we keep them all since backend only returns subscribed users
          return true;
        });

        console.log('Filtered recipients list:', recipientsList, 'Length:', recipientsList.length);
        setRecipients(recipientsList);
        setFilteredRecipients(recipientsList);
      })
      .catch(err => {
        console.error('Error fetching recipients:', err);
        setRecipients([]);
        setFilteredRecipients([]);
      });

    // Fetch email templates for template mode
    fetch('/api/admin/email-templates', {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    })
      .then(res => res.json())
      .then(templates => {
        setEmailTemplates(templates || []);
      })
      .catch(err => {
        console.error('Error fetching email templates:', err);
        setEmailTemplates([]);
      });
  }, []);

  // Filter products based on search term
  React.useEffect(() => {
    if (!productSearchTerm.trim()) {
      setFilteredProducts(products);
    } else {
      const search = productSearchTerm.toLowerCase();
      const filtered = products.filter(product =>
        product.name?.toLowerCase().includes(search) ||
        product.author?.toLowerCase().includes(search) ||
        product.artist?.toLowerCase().includes(search) ||
        product.description?.toLowerCase().includes(search) ||
        product.sku?.toLowerCase().includes(search) ||
        product.category?.toLowerCase().includes(search)
      );
      setFilteredProducts(filtered);
    }
  }, [productSearchTerm, products]);

  // Filter articles based on search term - same logic as Article.js
  React.useEffect(() => {
    if (!articleSearchTerm.trim()) {
      setFilteredArticles(blogPosts);
    } else {
      const search = articleSearchTerm.toLowerCase();
      const filtered = blogPosts.filter(post => {
        // Search in basic fields
        const titleMatch = (post.title || '').toLowerCase().includes(search);
        const descriptionMatch = (post.description || '').toLowerCase().includes(search);
        const excerptMatch = (post.excerpt || '').toLowerCase().includes(search);
        const authorMatch = (post.author || '').toLowerCase().includes(search);
        const slugMatch = (post.slug || '').toLowerCase().includes(search);
        const pageIdMatch = (post.page_id || '').toLowerCase().includes(search);

        // Search in tags - handle both string and array formats
        const tags = post.tags || [];
        const tagsString = typeof tags === 'string'
          ? tags
          : Array.isArray(tags)
            ? tags.join(' ')
            : '';
        const tagsMatch = tagsString.toLowerCase().includes(search);

        // Search in content
        const contentMatch = (post.content || '').toLowerCase().includes(search);

        // Search in metadata fields
        let metadataMatch = false;
        if (post.metadata && typeof post.metadata === 'object') {
          const metadataStr = JSON.stringify(post.metadata).toLowerCase();
          metadataMatch = metadataStr.includes(search);
        }

        return titleMatch || descriptionMatch || excerptMatch || authorMatch ||
          slugMatch || pageIdMatch || tagsMatch || contentMatch || metadataMatch;
      });
      setFilteredArticles(filtered);
    }
  }, [articleSearchTerm, blogPosts]);

  // Filter recipients based on search term - same logic as AdminUsers
  React.useEffect(() => {
    // Ensure recipients is an array before filtering
    if (!Array.isArray(recipients)) {
      console.warn('Recipients is not an array:', recipients);
      setFilteredRecipients([]);
      return;
    }

    if (!recipientSearchTerm.trim()) {
      setFilteredRecipients(recipients);
    } else {
      const search = recipientSearchTerm.toLowerCase();
      const filtered = recipients.filter(recipient => {
        return (
          recipient.email?.toLowerCase().includes(search) ||
          recipient.name?.toLowerCase().includes(search) ||
          recipient.type?.toLowerCase().includes(search) ||
          recipient.status?.toLowerCase().includes(search)
        );
      });
      setFilteredRecipients(filtered);
    }
  }, [recipientSearchTerm, recipients]);

  // Filter tunes based on search term
  React.useEffect(() => {
    if (!tuneSearchTerm.trim()) {
      setFilteredTunes(tunes);
    } else {
      const search = tuneSearchTerm.toLowerCase();
      const filtered = tunes.filter(tune =>
        tune.title?.toLowerCase().includes(search) ||
        tune.page_id?.toLowerCase().includes(search) ||
        tune.description?.toLowerCase().includes(search) ||
        tune.metadata?.toLowerCase().includes(search) ||
        (tune.tags && Array.isArray(tune.tags) && tune.tags.some(tag => tag.toLowerCase().includes(search)))
      );
      setFilteredTunes(filtered);
    }
  }, [tuneSearchTerm, tunes]);

  // Add blog post to newsletter
  const addBlogPost = (post) => {
    setNewsletterData(prev => {
      // Check if already added
      const alreadyAdded = prev.linkedItems.some(item => item.type === 'blog' && item.id === post.id);
      if (alreadyAdded) {
        alert('This article is already added to the newsletter.');
        return prev;
      }
      return {
        ...prev,
        linkedItems: [...prev.linkedItems, { type: 'blog', id: post.id, title: post.title, summary: post.excerpt || '', url: `/page/${post.slug}` }]
      };
    });
    // Clear search after adding
    setArticleSearchTerm('');
  };

  // Add product to newsletter
  const addProduct = (product) => {
    setNewsletterData(prev => {
      // Check if already added
      const alreadyAdded = prev.linkedItems.some(item => item.type === 'product' && item.id === product.id);
      if (alreadyAdded) {
        alert('This product is already added to the newsletter.');
        return prev;
      }
      return {
        ...prev,
        linkedItems: [...prev.linkedItems, { type: 'product', id: product.id, title: product.name, summary: product.description, url: `/product/${product.permalink || product.id}`, image: product.image }]
      };
    });
    // Clear search after adding
    setProductSearchTerm('');
  };

  // Add tune to newsletter
  const addTune = (tune) => {
    setNewsletterData(prev => {
      // Check if already added
      const alreadyAdded = prev.linkedItems.some(item => item.type === 'tune' && (item.id === tune.id || item.page_id === tune.page_id));
      if (alreadyAdded) {
        alert('This tune is already added to the newsletter.');
        return prev;
      }
      return {
        ...prev,
        linkedItems: [...prev.linkedItems, {
          type: 'tune',
          id: tune.id || tune.page_id,
          page_id: tune.page_id,
          title: tune.title || tune.page_id,
          summary: tune.description || tune.metadata || '',
          url: `/page/${tune.page_id}`
        }]
      };
    });
    // Clear search after adding
    setTuneSearchTerm('');
  };
  // Remove linked item
  const removeLinkedItem = (idx) => {
    setNewsletterData(prev => ({
      ...prev,
      linkedItems: prev.linkedItems.filter((_, i) => i !== idx)
    }));
  };
  // Reorder linked items
  const moveLinkedItem = (from, to) => {
    setNewsletterData(prev => {
      const items = [...prev.linkedItems];
      const [moved] = items.splice(from, 1);
      items.splice(to, 0, moved);
      return { ...prev, linkedItems: items };
    });
  };

  // Save newsletter as draft
  const handleSaveDraft = async () => {
    try {
      const token = localStorage.getItem('token');
      const payload = {
        title: newsletterData.title,
        subject: newsletterData.subject,
        content: newsletterData.content,
        tags: newsletterData.tags,
        linkedItems: newsletterData.linkedItems,
        selectedRecipients: newsletterData.selectedRecipients,
        status: 'draft',
        backgroundImage: newsletterData.backgroundImage,
        backgroundColor: newsletterData.backgroundColor,
        backgroundUrl: newsletterData.backgroundUrl,
        contentFormat: newsletterData.contentFormat || 'fragment'
      };

      const url = editingCampaignId
        ? `/api/newsletter/admin/campaigns/${editingCampaignId}`
        : '/api/newsletter/admin/campaigns';
      const method = editingCampaignId ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        credentials: 'include',
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        throw new Error('Failed to save draft');
      }

      const result = await response.json();
      const campaignId = result.campaignId || result.id;
      const wasNew = editingCampaignId == null;
      if (campaignId != null && wasNew) {
        setEditingCampaignId(Number(campaignId));
      }

      // Show success message with campaign ID
      if (window.confirm(`✅ Newsletter ${wasNew ? 'saved as draft' : 'updated'} successfully!\n\nCampaign ID: ${campaignId}\n\nClick OK to continue, or Cancel to view in Overview tab.`)) {
        // User clicked OK - do nothing, stay on create tab
      } else {
        // User clicked Cancel - switch to overview
        setActiveTab('overview');
        fetchDashboardStats();
      }

      // Optionally clear the form
      // setNewsletterData({...initial state});
    } catch (error) {
      console.error('Save draft error:', error);
      window.alert('❌ Failed to save draft\n\nError: ' + error.message + '\n\nPlease check the console for more details.');
    }
  };

  const [dashboardStats, setDashboardStats] = useState({
    totalSubscribers: 0,
    activeSubscribers: 0,
    registeredOptedIn: 0,
    guestSubscribers: 0,
    totalRegisteredUsers: 0,
    campaignsSent: 0,
    openRate: 0,
    clickRate: 0,
    recentCampaigns: []
  });
  const [, setLoadingStats] = useState(true);

  // Subscribers management state
  const [subscribersList, setSubscribersList] = useState([]);
  const [subscribersPagination, setSubscribersPagination] = useState({ page: 1, limit: 50, total: 0, pages: 0 });
  const [loadingSubscribers, setLoadingSubscribers] = useState(false);

  const fetchSendSafety = async () => {
    try {
      const response = await fetch('/api/newsletter/admin/send-safety', {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`
        }
      });
      if (response.ok) {
        const data = await response.json();
        setSendSafety(data);
        if (data.mode === 'test') {
          setNewsletterData((prev) => ({
            ...prev,
            recipientMode: 'selected',
            batchSize: Math.min(prev.batchSize || 100, data.maxTestRecipients || 2)
          }));
        }
      }
    } catch (error) {
      console.error('Error fetching send safety:', error);
    }
  };

  const fetchDashboardStats = async () => {
    try {
      const response = await fetch('/api/newsletter/admin/stats', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setDashboardStats(data);
      }
    } catch (error) {
      console.error('Error fetching dashboard stats:', error);
    } finally {
      setLoadingStats(false);
    }
  };

  const [editingSubscriber, setEditingSubscriber] = useState(null);

  const fetchSubscribers = async (page = 1) => {
    setLoadingSubscribers(true);
    try {
      const response = await fetch(`/api/newsletter/admin/subscribers?page=${page}&limit=50`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setSubscribersList(data.subscribers || []);
        setSubscribersPagination(data.pagination || { page: 1, limit: 50, total: 0, pages: 0 });
      }
    } catch (error) {
      console.error('Error fetching subscribers:', error);
    } finally {
      setLoadingSubscribers(false);
    }
  };

  const handleRemoveSubscriber = async (id) => {
    if (!window.confirm('Are you sure you want to remove this subscriber? For registered users, this will unsubscribe them.')) {
      return;
    }

    try {
      const response = await fetch(`/api/newsletter/admin/subscribers/${id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.ok) {
        alert('Subscriber removed successfully');
        fetchSubscribers(subscribersPagination.page);
        fetchDashboardStats(); // Update stats
      } else {
        const error = await response.json();
        alert('Failed to remove subscriber: ' + (error.details || error.error));
      }
    } catch (error) {
      console.error('Error removing subscriber:', error);
      alert('Failed to remove subscriber');
    }
  };

  const handleUpdateSubscriber = async (e) => {
    e.preventDefault();
    try {
      const response = await fetch(`/api/newsletter/admin/subscribers/${editingSubscriber.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify(editingSubscriber)
      });

      if (response.ok) {
        alert('Subscriber updated successfully');
        setEditingSubscriber(null);
        fetchSubscribers(subscribersPagination.page);
      } else {
        const error = await response.json();
        alert('Failed to update subscriber: ' + (error.details || error.error));
      }
    } catch (error) {
      console.error('Error updating subscriber:', error);
      alert('Failed to update subscriber');
    }
  };

  React.useEffect(() => {
    if (activeTab === 'subscribers') {
      fetchSubscribers();
    }
  }, [activeTab]);

  // Initial data fetch
  React.useEffect(() => {
    fetchDashboardStats();
    fetchSendSafety();
    // ... existing fetches
  }, []);

  // Access control is handled by ProtectedAdminRoute in App.js
  // No need for additional permission check here

  const renderOverview = () => (
    <div className="overview-section">
      <h3>Newsletter Overview</h3>

      <div style={{
        marginBottom: '20px',
        padding: '16px 18px',
        borderRadius: '8px',
        border: '1px solid var(--color-border)',
        background: 'var(--color-surface)',
        display: 'flex',
        flexWrap: 'wrap',
        gap: '12px',
        alignItems: 'center',
        justifyContent: 'space-between'
      }}>
        <div>
          <strong>Website Launch Announcement</strong>
          <div style={{ fontSize: '0.9em', color: 'var(--color-text-secondary)', marginTop: '4px' }}>
            Ready-made HTML email campaign. Send in batches of 100 / 200 / 500 / 1000; already-sent customers are tracked and skipped.
          </div>
        </div>
        <button
          type="button"
          className="btn-primary"
          onClick={handleLoadWebsiteAnnouncement}
          disabled={isCreatingFromTemplate}
        >
          {isCreatingFromTemplate ? 'Loading…' : '📣 Use Announcement Template'}
        </button>
      </div>

      <div className="stats-grid">
        <div className="stat-card" title="Unique emails who opted in (registered checkbox + guest list)">
          <h4>Active Subscribers</h4>
          <p className="stat-number">{dashboardStats.activeSubscribers}</p>
          <p className="stat-hint">Opted-in only (ready to receive)</p>
        </div>
        <div className="stat-card" title="Registered accounts with newsletter checkbox on">
          <h4>Registered Opted-In</h4>
          <p className="stat-number">{dashboardStats.registeredOptedIn ?? 0}</p>
          <p className="stat-hint">Users who checked subscribe</p>
        </div>
        <div className="stat-card" title="Guest emails on newsletter_subscribers">
          <h4>Guest List</h4>
          <p className="stat-number">{dashboardStats.guestSubscribers ?? 0}</p>
          <p className="stat-hint">Non-account subscribers</p>
        </div>
        <div className="stat-card" title="All active user accounts (not all subscribed)">
          <h4>Total Users</h4>
          <p className="stat-number">{dashboardStats.totalRegisteredUsers ?? 0}</p>
          <p className="stat-hint">Accounts — not all subscribed</p>
        </div>
        <div className="stat-card">
          <h4>Campaigns Sent</h4>
          <p className="stat-number">{dashboardStats.campaignsSent}</p>
        </div>
        <div className="stat-card">
          <h4>Average Open Rate</h4>
          <p className="stat-number">{dashboardStats.openRate}%</p>
        </div>
      </div>

      <div className="recent-campaigns">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
          <h4>Recent Campaigns {dashboardStats.recentCampaigns && dashboardStats.recentCampaigns.length > 0 && `(${dashboardStats.recentCampaigns.length})`}</h4>
          {selectedCampaigns.length > 0 && (
            <button
              className="btn-small"
              onClick={handleBulkDeleteCampaigns}
              style={{ background: 'var(--color-error)', color: 'white', padding: '8px 16px' }}
              title={`Delete ${selectedCampaigns.length} selected campaign(s)`}
            >
              🗑️ Delete Selected ({selectedCampaigns.length})
            </button>
          )}
        </div>

        {dashboardStats.recentCampaigns && dashboardStats.recentCampaigns.length > 0 ? (
          <>
            <div style={{ marginBottom: '10px', padding: '8px', background: 'var(--color-surface)', borderRadius: '4px' }}>
              <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={selectedCampaigns.length === dashboardStats.recentCampaigns.length}
                  onChange={toggleSelectAllCampaigns}
                  style={{ marginRight: '8px' }}
                />
                <span>Select All ({dashboardStats.recentCampaigns.length})</span>
              </label>
            </div>

            <div className="campaigns-list" style={{ maxHeight: '500px', overflowY: 'auto', paddingRight: '10px' }}>
              {dashboardStats.recentCampaigns.map(campaign => (
                <div key={campaign.id} className="campaign-item" style={{ display: 'flex', alignItems: 'center' }}>
                  <input
                    type="checkbox"
                    checked={selectedCampaigns.includes(campaign.id)}
                    onChange={() => toggleCampaignSelection(campaign.id)}
                    style={{ marginRight: '12px', cursor: 'pointer' }}
                    title="Select for bulk delete"
                  />
                  <div className="campaign-info" style={{ flex: 1 }}>
                    <h5>#{campaign.id} - {campaign.title}</h5>
                    <p>
                      Status: {campaign.status || 'draft'} · Date: {campaign.sent}
                      {campaign.contentFormat === 'full_html' ? ' · Full HTML' : ''}
                    </p>
                    <p style={{ fontSize: '0.85em', color: 'var(--color-text-secondary)', margin: '4px 0 0' }}>
                      Received: {campaign.alreadySent ?? 0}
                      {campaign.recipientCount ? ` / ${campaign.recipientCount}` : ''}
                      {typeof campaign.remaining === 'number' ? ` · Remaining: ${campaign.remaining}` : ''}
                    </p>
                  </div>
                  <div className="campaign-stats">
                    <span>Opens: {campaign.opens}</span>
                    <span>Clicks: {campaign.clicks}</span>
                  </div>
                  <div className="campaign-actions" style={{ marginLeft: '15px', display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                    <button
                      type="button"
                      className="btn-small"
                      onClick={() => handleEditCampaign(campaign.id)}
                      title="Edit this campaign in the composer (Manual Selection)"
                      style={{ background: 'var(--color-success)' }}
                    >
                      ✏️ Edit
                    </button>
                    <button
                      className="btn-small"
                      onClick={() => handlePreviewCampaign(campaign.id)}
                      title="Preview this campaign email"
                      style={{ background: 'var(--color-info)' }}
                    >
                      📧 Preview
                    </button>
                    <button
                      type="button"
                      className="btn-small"
                      onClick={() => setBatchSendModal({
                        campaignId: campaign.id,
                        title: campaign.title,
                        batchSize: 100,
                        remaining: campaign.remaining
                      })}
                      title={massSendsBlocked ? 'Batch sends blocked on localhost — use Create Email with 1–2 selected recipients' : 'Send next batch (skips already-sent)'}
                      style={{ background: massSendsBlocked ? 'var(--color-border)' : 'var(--color-primary)' }}
                      disabled={isSending || massSendsBlocked}
                    >
                      🚀 Send Batch
                    </button>
                    <button
                      type="button"
                      className="btn-small"
                      onClick={() => handleViewCampaignRecipients(campaign.id)}
                      title="See which customers already received this campaign"
                      disabled={loadingCampaignRecipients}
                    >
                      👥 Who Received
                    </button>
                    <button
                      className="btn-small"
                      onClick={() => handleDuplicateCampaign(campaign.id)}
                      title="Duplicate this campaign"
                    >
                      📋 Reuse
                    </button>
                    <button
                      className="btn-small"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteCampaign(campaign.id, campaign.title);
                      }}
                      title="Delete this campaign"
                      style={{ background: 'var(--color-error)' }}
                    >
                      🗑️ Delete
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </>
        ) : (
          <p>No recent campaigns found.</p>
        )}
      </div>
    </div>
  );

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setNewsletterData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleTagAdd = (tag) => {
    if (tag && !newsletterData.tags.includes(tag)) {
      setNewsletterData(prev => ({
        ...prev,
        tags: [...prev.tags, tag]
      }));
    }
  };

  const handleTagRemove = (tagToRemove) => {
    setNewsletterData(prev => ({
      ...prev,
      tags: prev.tags.filter(tag => tag !== tagToRemove)
    }));
  };

  // Automated newsletter handlers
  const handleAutomationConfigChange = (e) => {
    const { name, value, type, checked } = e.target;
    setAutomationConfig(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : (type === 'number' ? parseInt(value) : value)
    }));
  };

  const handlePreviewAutomated = async () => {
    setIsPreviewing(true);
    try {
      const token = localStorage.getItem('token');

      // Prepare payload with proper types
      const payload = {
        includeRecentArticles: automationConfig.includeRecentArticles === true,
        includeNewProducts: automationConfig.includeNewProducts === true,
        includePromotions: automationConfig.includePromotions === true,
        articleLimit: parseInt(automationConfig.articleLimit) || 3,
        productLimit: parseInt(automationConfig.productLimit) || 3,
        daysBack: parseInt(automationConfig.daysBack) || 30,
        theme: automationConfig.theme || 'celtic music',
        tone: automationConfig.tone || 'friendly and engaging',
        aiProvider: automationConfig.aiProvider || 'watsonx',
        templateType: automationConfig.templateType || 'standard'
      };

      const response = await fetch('/api/newsletter/admin/preview-automated', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        credentials: 'include',
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
        console.error('Preview error response:', errorData);
        throw new Error(errorData.error || errorData.details || 'Failed to generate preview');
      }

      const preview = await response.json();
      setAutomatedPreview(preview);
    } catch (error) {
      console.error('Preview error:', error);
      alert('Failed to generate preview: ' + error.message);
    } finally {
      setIsPreviewing(false);
    }
  };

  const handleGenerateAutomated = async () => {
    setIsGenerating(true);
    try {
      // Prepare payload with proper types
      const payload = {
        includeRecentArticles: automationConfig.includeRecentArticles === true,
        includeNewProducts: automationConfig.includeNewProducts === true,
        includePromotions: automationConfig.includePromotions === true,
        articleLimit: parseInt(automationConfig.articleLimit) || 3,
        productLimit: parseInt(automationConfig.productLimit) || 3,
        daysBack: parseInt(automationConfig.daysBack) || 30,
        theme: automationConfig.theme || 'celtic music',
        tone: automationConfig.tone || 'friendly and engaging',
        aiProvider: automationConfig.aiProvider || 'watsonx',
        templateType: automationConfig.templateType || 'standard',
        scheduleDate: automationConfig.scheduleDate && automationConfig.scheduleDate.trim()
          ? automationConfig.scheduleDate
          : null
      };

      const token = localStorage.getItem('token');
      const response = await fetch('/api/newsletter/admin/generate-automated', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        credentials: 'include',
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
        console.error('Generate error response:', errorData);
        throw new Error(errorData.error || errorData.details || 'Failed to generate newsletter');
      }

      const result = await response.json();
      alert(`Newsletter generated successfully! Campaign ID: ${result.campaignId}`);

      // Reset form
      setAutomationConfig({
        includeRecentArticles: true,
        includeNewProducts: true,
        includePromotions: false,
        articleLimit: 3,
        productLimit: 3,
        daysBack: 30,
        theme: 'celtic music',
        tone: 'friendly and engaging',
        scheduleDate: '',
        aiProvider: 'watsonx',
        templateType: 'standard'
      });
      setAutomatedPreview(null);
    } catch (error) {
      console.error('Generate error:', error);
      alert('Failed to generate newsletter: ' + error.message);
    } finally {
      setIsGenerating(false);
    }
  };

  // Generate AI content based on selected items
  const handleGenerateAIContent = async () => {
    if (newsletterData.linkedItems.length === 0 && automationConfig.aiProvider !== 'template') {
      alert('Please add at least one item (article, product, or tune) before generating AI content.');
      return;
    }

    // Handle Template Only mode
    if (automationConfig.aiProvider === 'template') {
      if (!newsletterData.templateId || !selectedTemplate) {
        alert('Please select a template first');
        return;
      }

      try {
        // Fill template variables from selected items
        let filledContent = selectedTemplate.html_content || '';
        const filledVariables = { ...newsletterData.templateVariables };

        // Auto-fill from first product if available
        const firstProduct = newsletterData.linkedItems.find(item => item.type === 'product');
        if (firstProduct) {
          const product = products.find(p => p.id === firstProduct.id);
          if (product) {
            filledVariables.product_name = product.name || '';
            filledVariables.product_description = product.description || '';
            filledVariables.product_price = product.price ? `$${product.price.toFixed(2)}` : '';
            filledVariables.product_url = `${window.location.origin}/product/${product.permalink || product.id}`;
            filledVariables.product_sku = product.sku || '';
          }
        }

        // Replace variables in content
        Object.entries(filledVariables).forEach(([key, value]) => {
          const regex = new RegExp(`{{${key}}}`, 'g');
          filledContent = filledContent.replace(regex, value || '');
        });

        // Update newsletter data with filled template
        setNewsletterData(prev => ({
          ...prev,
          title: selectedTemplate.subject || prev.title,
          subject: selectedTemplate.subject || prev.subject,
          content: filledContent,
          templateVariables: filledVariables
        }));

        alert('✅ Template applied! Please review the content before sending.');
        return;
      } catch (error) {
        console.error('Template fill error:', error);
        alert('Failed to apply template: ' + error.message);
        return;
      }
    }

    // Regular AI generation (Watsonx / OpenAI)
    setIsGenerating(true);
    try {
      const token = localStorage.getItem('token');

      const payload = {
        linkedItems: newsletterData.linkedItems,
        tone: automationConfig.tone || 'friendly and engaging',
        theme: automationConfig.theme || 'celtic music',
        aiProvider: automationConfig.aiProvider || 'watsonx',
        templateType: automationConfig.templateType || 'standard',
        temperature: newsletterData.aiTemperature || 0.7
      };

      console.log('Generating AI content with payload:', payload);

      const response = await fetch('/api/newsletter/admin/generate-ai-content', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        credentials: 'include',
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
        console.error('AI generation error response:', errorData);
        throw new Error(errorData.error || errorData.details || 'Failed to generate AI content');
      }

      const result = await response.json();

      // Use the generated title and subject directly - they already have dates and proper formatting
      // The backend's smart title generation handles this correctly now
      setNewsletterData(prev => ({
        ...prev,
        title: result.title || prev.title,
        subject: result.subject || prev.subject,
        content: result.content || prev.content,
        tags: result.tags ? [...new Set([...prev.tags, ...result.tags])] : prev.tags
      }));

      // alert('✅ AI content generated successfully! You can now edit the content before sending.');

    } catch (error) {
      console.error('AI generation error:', error);
      alert('Failed to generate AI content: ' + error.message);
    } finally {
      setIsGenerating(false);
    }
  };



  const handleSendNewsletter = async () => {
    // First save as draft/create campaign
    if (!newsletterData.title || !newsletterData.subject) {
      alert('Please provide a title and subject');
      return;
    }

    // Validate recipient selection
    if (!newsletterData.recipientMode) {
      alert('Please select a recipient option (All Subscribers or Specific Recipients)');
      return;
    }

    if (isTestSendMode) {
      if (newsletterData.recipientMode !== 'selected') {
        alert(`Local test mode: choose Specific Recipients and select 1–${maxTestRecipients} people.`);
        return;
      }
      if (
        newsletterData.selectedRecipients.length < 1 ||
        newsletterData.selectedRecipients.length > maxTestRecipients
      ) {
        alert(`Local test mode: select exactly 1–${maxTestRecipients} recipients (currently ${newsletterData.selectedRecipients.length}).`);
        return;
      }
    }

    if (newsletterData.recipientMode === 'selected' && newsletterData.selectedRecipients.length === 0) {
      alert('Please select at least one recipient');
      return;
    }

    setIsSending(true);
    try {
      const token = localStorage.getItem('token');

      let campaignId = editingCampaignId;

      if (campaignId) {
        const putRes = await fetch(`/api/newsletter/admin/campaigns/${campaignId}`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify(newsletterData)
        });
        if (!putRes.ok) {
          const errData = await putRes.json().catch(() => ({}));
          throw new Error(errData.error || errData.details || 'Failed to update campaign before sending');
        }
      } else {
        const saveResponse = await fetch('/api/newsletter/admin/campaigns', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify(newsletterData)
        });

        if (!saveResponse.ok) {
          throw new Error('Failed to save campaign before sending');
        }

        const savedCampaign = await saveResponse.json();
        campaignId = savedCampaign.campaignId || savedCampaign.id;
      }

      if (!campaignId) {
        throw new Error('No campaign ID for send');
      }

      // 2. Trigger send
      const sendResponse = await fetch(`/api/newsletter/admin/campaigns/${campaignId}/send`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          recipients: newsletterData.recipientMode === 'selected' ? newsletterData.selectedRecipients : null,
          batchSize: isTestSendMode ? null : (newsletterData.batchSize || 100),
          // Allow re-testing the same 1–2 addresses locally
          excludeAlreadySent: isTestSendMode ? false : (newsletterData.excludeAlreadySent !== false)
        })
      });

      if (!sendResponse.ok) {
        const errorData = await sendResponse.json();
        throw new Error(errorData.error || 'Failed to send newsletter');
      }

      const sendResult = await sendResponse.json();

      // Show success message using custom modal
      setSendSuccessModal({
        visible: true,
        campaignId: campaignId,
        recipientInfo: sendResult.planned != null
          ? `batch of ${sendResult.planned} (already sent: ${sendResult.alreadySent || 0}, remaining before batch: ${sendResult.remainingBefore || 0})`
          : (newsletterData.recipientMode === 'selected'
            ? newsletterData.selectedRecipients.length + ' selected recipients'
            : 'all active subscribers')
      });

      // Refresh stats
      fetchDashboardStats();

      // Reset form or redirect
      // setNewsletterData(initialNewsletterData);

    } catch (error) {
      console.error('Send newsletter error:', error);
      window.alert('❌ Failed to send newsletter\n\nError: ' + error.message + '\n\nPlease check the console for more details.');
    } finally {
      setIsSending(false);
    }
  };

  // Preview campaign email in new window
  const handlePreviewCampaign = async (campaignId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`/api/newsletter/admin/campaigns/${campaignId}/preview`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch campaign preview');
      }

      // Get HTML content
      const html = await response.text();

      // Open in new window
      const previewWindow = window.open('', '_blank', 'width=800,height=600');
      previewWindow.document.write(html);
      previewWindow.document.close();

    } catch (error) {
      console.error('Preview error:', error);
      window.alert('❌ Failed to preview campaign\n\nError: ' + error.message);
    }
  };

  const handleDuplicateCampaign = async (campaignId) => {
    if (!window.confirm('Are you sure you want to duplicate this campaign?')) return;

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`/api/newsletter/admin/campaigns/${campaignId}/duplicate`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to duplicate campaign');
      }

      const data = await response.json();
      const campaign = data.campaign;

      loadCampaignRowIntoEditor(campaign);
      setEditingCampaignId(Number(campaign.id));
      setActiveTab('create');
      window.scrollTo({ top: 0, behavior: 'smooth' });
      alert('Campaign duplicated and loaded into the editor. Save or send when ready.');

      // Refresh stats to show the new draft
      fetchDashboardStats();

    } catch (error) {
      console.error('Duplicate error:', error);
      alert('Failed to duplicate campaign: ' + error.message);
    }
  };

  const handleDeleteCampaign = (campaignId, campaignTitle) => {
    console.log('handleDeleteCampaign called for:', campaignId, campaignTitle);
    // Show custom confirmation dialog
    setDeleteConfirm({ id: campaignId, title: campaignTitle });
    console.log('deleteConfirm state set to:', { id: campaignId, title: campaignTitle });
  };

  // Bulk delete selected campaigns
  const handleBulkDeleteCampaigns = () => {
    if (selectedCampaigns.length === 0) {
      alert('Please select at least one campaign to delete.');
      return;
    }

    // Show custom confirmation dialog for bulk delete
    setDeleteConfirm({
      type: 'bulk',
      count: selectedCampaigns.length,
      ids: [...selectedCampaigns] // Copy array
    });
  };

  const confirmDelete = async () => {
    console.log('confirmDelete called. deleteConfirm state:', deleteConfirm);
    if (!deleteConfirm) return;

    // Handle Bulk Delete
    if (deleteConfirm.type === 'bulk') {
      const { ids } = deleteConfirm;
      setDeleteConfirm(null); // Close dialog

      try {
        const token = localStorage.getItem('token');
        let successCount = 0;
        let failCount = 0;

        for (const campaignId of ids) {
          try {
            const response = await fetch(`/api/newsletter/admin/campaigns/${campaignId}`, {
              method: 'DELETE',
              headers: {
                'Authorization': `Bearer ${token}`
              }
            });

            if (response.ok) {
              successCount++;
            } else {
              failCount++;
            }
          } catch (error) {
            console.error(`Failed to delete campaign ${campaignId}:`, error);
            failCount++;
          }
        }

        // Clear selections
        setSelectedCampaigns([]);

        // Show result
        if (successCount > 0) {
          alert(`✅ Successfully deleted ${successCount} campaign(s).${failCount > 0 ? `\n❌ Failed to delete ${failCount} campaign(s).` : ''}`);
        } else {
          alert(`❌ Failed to delete campaigns.`);
        }

        // Refresh dashboard
        fetchDashboardStats();

      } catch (error) {
        console.error('Bulk delete error:', error);
        alert('❌ Failed to delete campaigns: ' + error.message);
      }
      return;
    }

    // Handle Single Delete
    const { id: campaignId } = deleteConfirm;
    setDeleteConfirm(null); // Close dialog

    try {
      console.log('Sending DELETE request for campaign:', campaignId);
      const token = localStorage.getItem('token');
      const response = await fetch(`/api/newsletter/admin/campaigns/${campaignId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      console.log('DELETE response status:', response.status);

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Failed to delete campaign' }));
        console.error('DELETE failed with data:', errorData);
        throw new Error(errorData.error || 'Failed to delete campaign');
      }

      console.log('Campaign deleted successfully. Refreshing stats...');
      alert('✅ Campaign deleted successfully!');

      // Refresh stats to reflect deletion
      fetchDashboardStats();

    } catch (error) {
      console.error('Delete error:', error);
      alert('❌ Failed to delete campaign: ' + error.message);
    }
  };

  const cancelDelete = () => {
    console.log('cancelDelete called');
    setDeleteConfirm(null);
  };

  // Preview current newsletter
  const handlePreviewNewsletter = async () => {
    if (!newsletterData.title || !newsletterData.content) {
      alert('Please add a title and content before previewing.');
      return;
    }

    try {
      // Create a temporary preview by generating the HTML
      const token = localStorage.getItem('token');

      // Create a temporary campaign object for preview
      const previewCampaign = {
        name: newsletterData.title,
        subject: newsletterData.subject,
        content: newsletterData.content,
        linked_items: JSON.stringify(newsletterData.linkedItems),
        background_image: newsletterData.backgroundImage,
        background_color: newsletterData.backgroundColor,
        background_url: newsletterData.backgroundUrl,
        contentFormat: newsletterData.contentFormat || 'fragment'
      };

      // Generate preview HTML
      const response = await fetch('/api/newsletter/admin/campaigns/preview-html', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(previewCampaign)
      });

      if (!response.ok) {
        throw new Error('Failed to generate preview');
      }

      const html = await response.text();

      // Open preview in new window
      const previewWindow = window.open('', '_blank', 'width=800,height=600');
      previewWindow.document.write(html);
      previewWindow.document.close();

    } catch (error) {
      console.error('Preview error:', error);
      alert('❌ Failed to preview newsletter: ' + error.message);
    }
  };



  // Toggle campaign selection
  const toggleCampaignSelection = (campaignId) => {
    setSelectedCampaigns(prev =>
      prev.includes(campaignId)
        ? prev.filter(id => id !== campaignId)
        : [...prev, campaignId]
    );
  };

  // Select all campaigns
  const toggleSelectAllCampaigns = () => {
    if (selectedCampaigns.length === dashboardStats.recentCampaigns.length) {
      setSelectedCampaigns([]);
    } else {
      setSelectedCampaigns(dashboardStats.recentCampaigns.map(c => c.id));
    }
  };

  // ===== UNIFIED EMAIL CREATOR =====
  const renderUnifiedEmailCreator = () => (
    <div className="unified-email-creator">
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: '12px',
          marginBottom: '8px'
        }}
      >
        <h3 style={{ margin: 0 }}>📧 Create Email Campaign</h3>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
          {editingCampaignId != null && (
            <span
              style={{
                fontSize: '0.9rem',
                padding: '6px 12px',
                borderRadius: '6px',
                background: 'var(--color-primary-dim)',
                border: '1px solid var(--color-primary)',
                color: 'var(--color-text-primary)'
              }}
            >
              Editing campaign #{editingCampaignId}
            </span>
          )}
          <button
            type="button"
            className="btn-small"
            onClick={handleStartNewCampaign}
            title="Clear the form and start a new campaign"
            style={{ background: 'var(--color-surface)', color: '#fff' }}
          >
            ➕ New campaign
          </button>
        </div>
      </div>

      {/* Mode Selector */}
      <div className="mode-selector" style={{
        marginBottom: '30px',
        padding: '20px',
        background: 'var(--color-surface)',
        borderRadius: '8px',
        border: '2px solid var(--color-border)'
      }}>
        <label style={{ display: 'block', marginBottom: '15px', fontWeight: '600', fontSize: '1.1em' }}>
          Content Mode:
        </label>
        <div style={{ display: 'flex', gap: '15px', flexWrap: 'wrap' }}>
          <label
            style={{
              flex: '1 1 200px',
              padding: '15px 20px',
              border: `2px solid ${newsletterData.contentMode === 'manual' ? 'var(--color-primary)' : 'var(--color-border)'}`,
              borderRadius: '8px',
              cursor: 'pointer',
              background: newsletterData.contentMode === 'manual' ? 'var(--color-primary-dim)' : 'transparent',
              transition: 'all 0.2s'
            }}
          >
            <input
              type="radio"
              name="contentMode"
              value="manual"
              checked={newsletterData.contentMode === 'manual'}
              onChange={(e) => setNewsletterData(prev => ({ ...prev, contentMode: e.target.value }))}
              style={{ marginRight: '10px' }}
            />
            <strong>📝 Manual Selection</strong>
            <div style={{ fontSize: '0.85em', color: '#888', marginTop: '5px' }}>
              Handpick articles, products, and tunes
            </div>
          </label>

          <label
            style={{
              flex: '1 1 200px',
              padding: '15px 20px',
              border: `2px solid ${newsletterData.contentMode === 'automated' ? 'var(--color-primary)' : 'var(--color-border)'}`,
              borderRadius: '8px',
              cursor: 'pointer',
              background: newsletterData.contentMode === 'automated' ? 'var(--color-primary-dim)' : 'transparent',
              transition: 'all 0.2s'
            }}
          >
            <input
              type="radio"
              name="contentMode"
              value="automated"
              checked={newsletterData.contentMode === 'automated'}
              onChange={(e) => setNewsletterData(prev => ({ ...prev, contentMode: e.target.value }))}
              style={{ marginRight: '10px' }}
            />
            <strong>🤖 Automated Discovery</strong>
            <div style={{ fontSize: '0.85em', color: '#888', marginTop: '5px' }}>
              Auto-discover recent content
            </div>
          </label>

          <label
            style={{
              flex: '1 1 200px',
              padding: '15px 20px',
              border: `2px solid ${newsletterData.contentMode === 'template' ? 'var(--color-primary)' : 'var(--color-border)'}`,
              borderRadius: '8px',
              cursor: 'pointer',
              background: newsletterData.contentMode === 'template' ? 'var(--color-primary-dim)' : 'transparent',
              transition: 'all 0.2s'
            }}
          >
            <input
              type="radio"
              name="contentMode"
              value="template"
              checked={newsletterData.contentMode === 'template'}
              onChange={(e) => setNewsletterData(prev => ({ ...prev, contentMode: e.target.value }))}
              style={{ marginRight: '10px' }}
            />
            <strong>📝 Create Template</strong>
            <div style={{ fontSize: '0.85em', color: '#888', marginTop: '5px' }}>
              Create & manage email templates
            </div>
          </label>
        </div>
      </div>

      {/* Render appropriate content based on mode */}
      {newsletterData.contentMode === 'manual' && renderCreateNewsletter()}
      {newsletterData.contentMode === 'automated' && renderAutomatedNewsletter()}
      {newsletterData.contentMode === 'template' && renderTemplateMode()}
    </div>
  );

  // ===== TEMPLATE MODE RENDER =====
  const renderTemplateMode = () => {
    // Handler for template selection
    const handleTemplateSelect = async (templateId) => {
      if (!templateId) {
        setNewsletterData(prev => ({ ...prev, templateId: '', templateVariables: {} }));
        setSelectedTemplate(null);
        return;
      }

      try {
        const token = localStorage.getItem('token');
        const response = await fetch(`/api/admin/email-templates/${templateId}`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });

        if (response.ok) {
          const template = await response.json();
          let templateVars = {};
          try {
            templateVars = typeof template.variables === 'string'
              ? JSON.parse(template.variables)
              : (template.variables || {});
          } catch (e) {
            console.error('Error parsing template variables:', e);
          }

          setNewsletterData(prev => ({
            ...prev,
            templateId: templateId,
            subject: template.subject,
            htmlContent: template.html_content,
            textContent: template.text_content || '',
            templateVariables: templateVars
          }));
          setSelectedTemplate(template);
        }
      } catch (error) {
        console.error('Error loading template:', error);
        alert('Failed to load template');
      }
    };

    // Handler for template variable changes
    const handleTemplateVariableChange = (key, value) => {
      setNewsletterData(prev => ({
        ...prev,
        templateVariables: {
          ...prev.templateVariables,
          [key]: value
        }
      }));
    };

    // Get friendly field labels
    const getFieldLabel = (key) => {
      const labels = {
        product_name: 'Product Name',
        product_description: 'Product Description',
        product_price: 'Product Price',
        product_image_url: 'Product Image URL',
        product_url: 'Product URL',
        product_sku: 'Product SKU',
        feature_1: 'Feature 1',
        feature_2: 'Feature 2',
        feature_3: 'Feature 3'
      };
      return labels[key] || key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
    };

    return (
      <div className="template-mode-section">
        <h4>📝 Create Email Template</h4>
        <p style={{ color: '#888', marginBottom: '1.5rem' }}>
          Create reusable email templates with variables. Use these templates in Manual Selection mode when creating newsletters.
        </p>

        {/* Template Selector */}
        <div style={{ marginBottom: '1.5rem' }}>
          <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600' }}>
            Select Template
          </label>
          <select
            value={newsletterData.templateId}
            onChange={(e) => handleTemplateSelect(e.target.value)}
            style={{ width: '100%', padding: '0.5rem', borderRadius: '4px', background: 'var(--color-surface)', color: 'inherit', border: '1px solid var(--color-border)' }}
          >
            <option value="">-- Select a Template --</option>
            {emailTemplates.map(template => (
              <option key={template.id} value={template.id}>{template.name}</option>
            ))}
          </select>
        </div>

        {/* Template Variables */}
        {selectedTemplate && newsletterData.templateVariables && Object.keys(newsletterData.templateVariables).length > 0 && (
          <div style={{ marginBottom: '1.5rem', padding: '1rem', background: 'var(--color-surface)', borderRadius: '4px', border: '2px solid var(--color-primary)' }}>
            <h4 style={{ marginTop: 0, marginBottom: '1rem', color: 'var(--color-primary)' }}>📝 Customize Template Content</h4>
            <p style={{ color: '#bbb', fontSize: '0.9rem', marginBottom: '1rem' }}>
              Fill in the fields below to customize your email. These values will be automatically inserted into the template.
            </p>

            {Object.entries(newsletterData.templateVariables).map(([key, value]) => (
              <div key={key} style={{ marginBottom: '1rem' }}>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600' }}>
                  {getFieldLabel(key)}
                </label>
                {/* Product selector for product_name field */}
                {key === 'product_name' && products.length > 0 ? (
                  <select
                    value={products.find(p => p.name === value)?.id || ''}
                    onChange={(e) => {
                      const product = products.find(p => p.id === parseInt(e.target.value));
                      if (product) {
                        // Auto-fill product variables
                        const updatedVars = {
                          ...newsletterData.templateVariables,
                          product_name: product.name || '',
                          product_description: product.description || '',
                          product_price: product.price ? `$${product.price.toFixed(2)}` : '',
                          product_url: `${window.location.origin}/product/${product.permalink || product.id}`,
                          product_sku: product.sku || ''
                        };
                        setNewsletterData(prev => ({ ...prev, templateVariables: updatedVars }));
                      }
                    }}
                    style={{ width: '100%', padding: '0.5rem', borderRadius: '4px', background: 'var(--color-background)', color: 'inherit', border: '1px solid var(--color-border)' }}
                  >
                    <option value="">-- Select a Product --</option>
                    {products.map(product => (
                      <option key={product.id} value={product.id}>
                        {product.name} {product.sku ? `(${product.sku})` : ''}
                      </option>
                    ))}
                  </select>
                ) : key.includes('content') || key.includes('message') || key.includes('description') ? (
                  <textarea
                    value={value || ''}
                    onChange={(e) => handleTemplateVariableChange(key, e.target.value)}
                    placeholder={`Enter ${getFieldLabel(key).toLowerCase()}...`}
                    rows="3"
                    style={{ width: '100%', padding: '0.5rem', borderRadius: '4px', fontFamily: 'inherit', background: 'var(--color-background)', color: 'inherit', border: '1px solid var(--color-border)' }}
                  />
                ) : (
                  <input
                    type="text"
                    value={value || ''}
                    onChange={(e) => handleTemplateVariableChange(key, e.target.value)}
                    placeholder={`Enter ${getFieldLabel(key).toLowerCase()}...`}
                    style={{ width: '100%', padding: '0.5rem', borderRadius: '4px', background: 'var(--color-background)', color: 'inherit', border: '1px solid var(--color-border)' }}
                  />
                )}
              </div>
            ))}
          </div>
        )}

        {/* Subject */}
        <div style={{ marginBottom: '1.5rem' }}>
          <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600' }}>
            Subject <span style={{ color: 'var(--color-error)' }}>*</span>
          </label>
          <input
            type="text"
            value={newsletterData.subject}
            onChange={(e) => setNewsletterData(prev => ({ ...prev, subject: e.target.value }))}
            placeholder="Email subject line"
            style={{ width: '100%', padding: '0.5rem', borderRadius: '4px', background: 'var(--color-surface)', color: 'inherit', border: '1px solid var(--color-border)' }}
          />
          <small style={{ color: '#888', fontSize: '0.85rem' }}>
            Available variables: {'{'}{ }firstName{'}'}, {'{'}{ }lastName{'}'}, {'{'}{ }username{'}'}, {'{'}{ }company_name{'}'}
          </small>
        </div>

        {/* HTML Content */}
        <div style={{ marginBottom: '1.5rem' }}>
          <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600' }}>
            HTML Content <span style={{ color: 'var(--color-error)' }}>*</span>
          </label>
          <textarea
            value={newsletterData.htmlContent}
            onChange={(e) => setNewsletterData(prev => ({ ...prev, htmlContent: e.target.value }))}
            placeholder="HTML email content..."
            rows="12"
            style={{ width: '100%', padding: '0.5rem', borderRadius: '4px', fontFamily: 'monospace', background: 'var(--color-surface)', color: 'inherit', border: '1px solid var(--color-border)' }}
          />
          <small style={{ color: '#888', fontSize: '0.85rem' }}>
            Available variables: {'{'}{ }firstName{'}'}, {'{'}{ }lastName{'}'}, {'{'}{ }username{'}'}, {'{'}{ }company_name{'}'}
          </small>
        </div>

        {/* Text Content (Optional) */}
        <div style={{ marginBottom: '1.5rem' }}>
          <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600' }}>
            Text Content (Optional - plain text fallback)
          </label>
          <textarea
            value={newsletterData.textContent}
            onChange={(e) => setNewsletterData(prev => ({ ...prev, textContent: e.target.value }))}
            placeholder="Plain text version..."
            rows="6"
            style={{ width: '100%', padding: '0.5rem', borderRadius: '4px', fontFamily: 'monospace', background: 'var(--color-surface)', color: 'inherit', border: '1px solid var(--color-border)' }}
          />
        </div>

        {/* Action Buttons for Template Mode */}
        <div style={{ marginTop: '2rem', display: 'flex', gap: '15px', justifyContent: 'flex-end' }}>
          <button
            type="button"
            className="btn-secondary"
            onClick={() => {
              // Preview template
              const previewWindow = window.open('', '_blank', 'width=800,height=600');
              previewWindow.document.write(newsletterData.htmlContent || '<p>No content to preview</p>');
              previewWindow.document.close();
            }}
            style={{ padding: '12px 24px' }}
          >
            👁️ Preview Template
          </button>
          <button
            type="button"
            className="btn-primary"
            onClick={async () => {
              // Save template
              if (!newsletterData.subject || !newsletterData.htmlContent) {
                alert('Please provide a subject and HTML content');
                return;
              }

              try {
                const token = localStorage.getItem('token');
                const templateData = {
                  name: newsletterData.subject, // Use subject as template name
                  subject: newsletterData.subject,
                  html_content: newsletterData.htmlContent,
                  text_content: newsletterData.textContent,
                  variables: JSON.stringify(newsletterData.templateVariables)
                };

                const url = newsletterData.templateId
                  ? `/api/admin/email-templates/${newsletterData.templateId}`
                  : '/api/admin/email-templates';

                const response = await fetch(url, {
                  method: newsletterData.templateId ? 'PUT' : 'POST',
                  headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                  },
                  body: JSON.stringify(templateData)
                });

                if (response.ok) {
                  alert('✅ Template saved successfully!');
                  // Refresh template list
                  const templatesResponse = await fetch('/api/admin/email-templates', {
                    headers: { 'Authorization': `Bearer ${token}` }
                  });
                  if (templatesResponse.ok) {
                    const templates = await templatesResponse.json();
                    setEmailTemplates(templates);
                  }
                } else {
                  const error = await response.json();
                  alert('Failed to save template: ' + (error.error || error.details));
                }
              } catch (error) {
                console.error('Save template error:', error);
                alert('Failed to save template: ' + error.message);
              }
            }}
            style={{ padding: '12px 24px' }}
          >
            💾 Save Template
          </button>
        </div>
      </div>
    );
  };




  const renderCreateNewsletter = () => (
    <div className="create-newsletter-section">
      <h3>Create Newsletter</h3>

      <form className="newsletter-form">
        {/* Basic Info, Content, Tags, Scheduling moved below AI Generator */}

        {/* Add Articles/Blog Posts */}
        <div className="newsletter-link-section">
          <h4>Add Article/Blog</h4>
          <div className="search-wrapper" style={{ marginBottom: '15px' }}>
            <input
              type="text"
              placeholder="Search articles by title, author, description, tags, or content..."
              value={articleSearchTerm}
              onChange={(e) => setArticleSearchTerm(e.target.value)}
              style={{
                width: '100%',
                padding: '10px 15px',
                border: '2px solid var(--color-border)',
                borderRadius: '5px',
                fontSize: '14px',
                background: 'var(--color-input-bg))',
                color: 'var(--color-text-primary)'
              }}
            />
          </div>
          <div className="newsletter-link-list" style={{ maxHeight: '300px', overflowY: 'auto' }}>
            {filteredArticles.length > 0 ? (
              filteredArticles.map(post => {
                const isAdded = newsletterData.linkedItems.some(item => item.type === 'blog' && item.id === post.id);
                return (
                  <button
                    type="button"
                    key={post.id}
                    onClick={() => addBlogPost(post)}
                    disabled={isAdded}
                    style={{
                      display: 'block',
                      width: '100%',
                      textAlign: 'left',
                      padding: '10px',
                      marginBottom: '5px',
                      border: `1px solid ${isAdded ? 'var(--color-success)' : 'var(--color-border)'}`,
                      borderRadius: '5px',
                      background: isAdded ? 'var(--color-success))' : 'var(--color-surface)',
                      color: 'var(--color-text-primary)',
                      cursor: isAdded ? 'default' : 'pointer',
                      transition: 'background 0.2s',
                      opacity: isAdded ? 0.8 : 1
                    }}
                    onMouseEnter={(e) => {
                      if (!isAdded) {
                        e.currentTarget.style.background = 'var(--color-surface-hover)';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (!isAdded) {
                        e.currentTarget.style.background = 'var(--color-surface)';
                      }
                    }}
                  >
                    <div style={{ fontWeight: 'bold', marginBottom: '5px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span>{post.title || post.page_id}</span>
                      {isAdded && <span style={{ fontSize: '12px', color: 'var(--color-success)' }}>✓ Added</span>}
                    </div>
                    {post.author && (
                      <div style={{ fontSize: '11px', color: 'var(--color-text-secondary)', marginBottom: '3px', fontStyle: 'italic' }}>
                        by {post.author}
                      </div>
                    )}
                    {(post.excerpt || post.description) && (
                      <div style={{ fontSize: '12px', color: 'var(--color-text-secondary)', marginBottom: '3px' }}>
                        {(post.excerpt || post.description).substring(0, 100)}...
                      </div>
                    )}
                    {post.tags && (Array.isArray(post.tags) ? post.tags.length > 0 : post.tags) && (
                      <div style={{ fontSize: '11px', color: 'var(--color-text-secondary)', marginTop: '3px' }}>
                        Tags: {typeof post.tags === 'string' ? post.tags : post.tags.join(', ')}
                      </div>
                    )}
                  </button>
                );
              })
            ) : (
              <div style={{ padding: '20px', textAlign: 'center', color: 'var(--color-text-secondary)' }}>
                {articleSearchTerm ? 'No articles found matching your search.' : 'No articles available.'}
              </div>
            )}
          </div>
        </div>

        {/* Add Products */}
        <div className="newsletter-link-section">
          <h4>Add Product</h4>
          <div className="search-wrapper" style={{ marginBottom: '15px' }}>
            <input
              type="text"
              placeholder="Search products by name, author, SKU, or category..."
              value={productSearchTerm}
              onChange={(e) => setProductSearchTerm(e.target.value)}
              style={{
                width: '100%',
                padding: '10px 15px',
                border: '2px solid var(--color-border)',
                borderRadius: '5px',
                fontSize: '14px',
                background: 'var(--color-input-bg))',
                color: 'var(--color-text-primary)'
              }}
            />
          </div>
          <div className="newsletter-link-list" style={{ maxHeight: '300px', overflowY: 'auto' }}>
            {filteredProducts.length > 0 ? (
              filteredProducts.map(product => {
                const isAdded = newsletterData.linkedItems.some(item => item.type === 'product' && item.id === product.id);
                return (
                  <button
                    type="button"
                    key={product.id}
                    onClick={() => addProduct(product)}
                    disabled={isAdded}
                    style={{
                      display: 'block',
                      width: '100%',
                      textAlign: 'left',
                      padding: '10px',
                      marginBottom: '5px',
                      border: `1px solid ${isAdded ? 'var(--color-success)' : 'var(--color-border)'}`,
                      borderRadius: '5px',
                      background: isAdded ? 'var(--color-success))' : 'var(--color-surface)',
                      color: 'var(--color-text-primary)',
                      cursor: isAdded ? 'default' : 'pointer',
                      transition: 'background 0.2s',
                      opacity: isAdded ? 0.8 : 1
                    }}
                    onMouseEnter={(e) => {
                      if (!isAdded) {
                        e.currentTarget.style.background = 'var(--color-surface-hover)';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (!isAdded) {
                        e.currentTarget.style.background = 'var(--color-surface)';
                      }
                    }}
                  >
                    <div style={{ fontWeight: 'bold', marginBottom: '5px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span>{product.name}</span>
                      {isAdded && <span style={{ fontSize: '12px', color: 'var(--color-success)' }}>✓ Added</span>}
                    </div>
                    <div style={{ fontSize: '12px', color: 'var(--color-text-secondary)' }}>
                      {product.author && `${product.author} • `}
                      {product.category && `${product.category} • `}
                      {product.price_cents != null ? `$${(product.price_cents / 100).toFixed(2)}` : ''}
                    </div>
                  </button>
                );
              })
            ) : (
              <div style={{ padding: '20px', textAlign: 'center', color: 'var(--color-text-secondary)' }}>
                {productSearchTerm ? 'No products found matching your search.' : 'No products available.'}
              </div>
            )}
          </div>
        </div>

        {/* Add Tunes */}
        <div className="newsletter-link-section">
          <h4>Add Tune</h4>
          <div className="search-wrapper" style={{ marginBottom: '15px' }}>
            <input
              type="text"
              placeholder="Search tunes by title, page ID, or description..."
              value={tuneSearchTerm}
              onChange={(e) => setTuneSearchTerm(e.target.value)}
              style={{
                width: '100%',
                padding: '10px 15px',
                border: '2px solid var(--color-border)',
                borderRadius: '5px',
                fontSize: '14px',
                background: 'var(--color-input-bg))',
                color: 'var(--color-text-primary)'
              }}
            />
          </div>
          <div className="newsletter-link-list" style={{ maxHeight: '300px', overflowY: 'auto' }}>
            {filteredTunes.length > 0 ? (
              filteredTunes.map(tune => {
                const isAdded = newsletterData.linkedItems.some(item =>
                  item.type === 'tune' && (item.id === tune.id || item.page_id === tune.page_id)
                );
                return (
                  <button
                    type="button"
                    key={tune.id || tune.page_id}
                    onClick={() => addTune(tune)}
                    disabled={isAdded}
                    style={{
                      display: 'block',
                      width: '100%',
                      textAlign: 'left',
                      padding: '10px',
                      marginBottom: '5px',
                      border: `1px solid ${isAdded ? 'var(--color-success)' : 'var(--color-border)'}`,
                      borderRadius: '5px',
                      background: isAdded ? 'var(--color-success))' : 'var(--color-surface)',
                      color: 'var(--color-text-primary)',
                      cursor: isAdded ? 'default' : 'pointer',
                      transition: 'background 0.2s',
                      opacity: isAdded ? 0.8 : 1
                    }}
                    onMouseEnter={(e) => {
                      if (!isAdded) {
                        e.currentTarget.style.background = 'var(--color-surface-hover)';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (!isAdded) {
                        e.currentTarget.style.background = 'var(--color-surface)';
                      }
                    }}
                  >
                    <div style={{ fontWeight: 'bold', marginBottom: '5px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span>{tune.title || tune.page_id}</span>
                      {isAdded && <span style={{ fontSize: '12px', color: 'var(--color-success)' }}>✓ Added</span>}
                    </div>
                    {tune.description && (
                      <div style={{ fontSize: '12px', color: 'var(--color-text-secondary)', marginBottom: '3px' }}>
                        {tune.description.substring(0, 100)}...
                      </div>
                    )}
                    {tune.page_id && (
                      <div style={{ fontSize: '11px', color: 'var(--color-text-secondary)' }}>
                        ID: {tune.page_id}
                      </div>
                    )}
                  </button>
                );
              })
            ) : (
              <div style={{ padding: '20px', textAlign: 'center', color: 'var(--color-text-secondary)' }}>
                {tuneSearchTerm ? 'No tunes found matching your search.' : 'No tunes available.'}
              </div>
            )}
          </div>
        </div>

        {/* Add Custom Link */}
        <div className="newsletter-link-section">
          <h4>Add Custom Link</h4>
          <input placeholder="Title" value={customLink.title} onChange={e => setCustomLink({ ...customLink, title: e.target.value })} />
          <input placeholder="URL" value={customLink.url} onChange={e => setCustomLink({ ...customLink, url: e.target.value })} />
          <input placeholder="Description" value={customLink.description} onChange={e => setCustomLink({ ...customLink, description: e.target.value })} />
          {customLink.error && <div className="custom-link-error" style={{ color: 'red', marginTop: 4 }}>{customLink.error}</div>}
          <button type="button" onClick={() => {
            // Enhanced addCustomLink logic with error feedback
            const title = customLink.title.trim();
            const url = customLink.url.trim();
            if (!title || !url) {
              setCustomLink(cl => ({ ...cl, error: 'Both title and URL are required.' }));
              return;
            }
            setNewsletterData(prev => ({
              ...prev,
              linkedItems: [...prev.linkedItems, { type: 'custom', title, url, description: customLink.description.trim() }]
            }));
            setCustomLink({ title: '', url: '', description: '', error: '' });
          }}>
            Add Link
          </button>
        </div>
        {/* Linked Items List */}
        <div className="newsletter-link-section">
          <h4>Linked Items</h4>
          <ul>
            {newsletterData.linkedItems.map((item, idx) => (
              <li key={idx}>
                <span>[{item.type}] {item.title} - <a href={item.url} target="_blank" rel="noopener noreferrer">{item.url}</a></span>
                <button onClick={() => removeLinkedItem(idx)}>Remove</button>
                {idx > 0 && <button onClick={() => moveLinkedItem(idx, idx - 1)}>↑</button>}
                {idx < newsletterData.linkedItems.length - 1 && <button onClick={() => moveLinkedItem(idx, idx + 1)}>↓</button>}
              </li>
            ))}
          </ul>
        </div>

        {/* AI Content Generation Section */}
        <div className="newsletter-link-section" style={{
          background: 'linear-gradient(135deg, rgba(74, 144, 226, 0.1), rgba(91, 156, 235, 0.1))',
          border: '2px solid rgba(74, 144, 226, 0.3)',
          borderRadius: '8px',
          padding: '20px',
          marginBottom: '20px'
        }}>
          <h4 style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: 0 }}>
            <span>🤖</span> AI Content Generator
          </h4>
          <p style={{ fontSize: '13px', color: 'var(--color-text-secondary)', marginBottom: '15px' }}>
            Generate newsletter content based on your selected items using AI. You can edit the generated content afterward.
          </p>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '15px', marginBottom: '15px' }}>
            <div className="form-group" style={{ margin: 0 }}>
              <label htmlFor="aiTone" style={{ fontSize: '13px' }}>Tone</label>
              <select
                id="aiTone"
                value={automationConfig.tone}
                onChange={(e) => setAutomationConfig(prev => ({ ...prev, tone: e.target.value }))}
                style={{
                  width: '100%',
                  padding: '8px',
                  borderRadius: '4px',
                  border: '1px solid var(--color-border)',
                  background: 'var(--color-input-bg)',
                  color: 'var(--color-text-primary)'
                }}
              >
                <option value="friendly and engaging">Friendly & Engaging</option>
                <option value="professional">Professional</option>
                <option value="exciting">Exciting</option>
                <option value="casual">Casual</option>
                <option value="formal">Formal</option>
              </select>
            </div>

            <div className="form-group" style={{ margin: 0 }}>
              <label htmlFor="aiTheme" style={{ fontSize: '13px' }}>Theme</label>
              <input
                type="text"
                id="aiTheme"
                value={automationConfig.theme}
                onChange={(e) => setAutomationConfig(prev => ({ ...prev, theme: e.target.value }))}
                placeholder="e.g., celtic music"
                style={{
                  width: '100%',
                  padding: '8px',
                  borderRadius: '4px',
                  border: '1px solid var(--color-border)',
                  background: 'var(--color-input-bg)',
                  color: 'var(--color-text-primary)'
                }}
              />
            </div>

            <div className="form-group" style={{ margin: 0 }}>
              <label htmlFor="aiProvider" style={{ fontSize: '13px' }}>AI Provider</label>
              <select
                id="aiProvider"
                value={automationConfig.aiProvider}
                onChange={(e) => setAutomationConfig(prev => ({ ...prev, aiProvider: e.target.value }))}
                style={{
                  width: '100%',
                  padding: '8px',
                  borderRadius: '4px',
                  border: '1px solid var(--color-border)',
                  background: 'var(--color-input-bg)',
                  color: 'var(--color-text-primary)'
                }}
              >
                <option value="watsonx">WatsonX (IBM)</option>
                <option value="openai">OpenAI</option>
                <option value="template">Template Only</option>
              </select>
            </div>

            {automationConfig.aiProvider === 'template' && (
              <div style={{ marginTop: '1rem', padding: '1rem', background: 'var(--color-surface)', borderRadius: '4px', border: '2px solid var(--color-primary)' }}>
                <h4 style={{ marginTop: 0, marginBottom: '1rem', color: 'var(--color-primary)' }}>📄 Use Email Template</h4>

                {/* Template Selector */}
                <div className="form-group" style={{ marginBottom: '1rem' }}>
                  <label htmlFor="emailTemplate" style={{ fontSize: '13px', fontWeight: '600' }}>Select Template</label>
                  <select
                    id="emailTemplate"
                    value={newsletterData.templateId || ''}
                    onChange={async (e) => {
                      const templateId = e.target.value;
                      if (!templateId) {
                        setNewsletterData(prev => ({
                          ...prev,
                          templateId: '',
                          templateVariables: {},
                          subject: `🎻 Cranfordpub Newsletter 🎻 - ${new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}`,
                          content: ''
                        }));
                        setSelectedTemplate(null);
                        return;
                      }

                      try {
                        const token = localStorage.getItem('token');
                        const response = await fetch(`/api/admin/email-templates/${templateId}`, {
                          headers: { 'Authorization': `Bearer ${token}` }
                        });

                        if (response.ok) {
                          const template = await response.json();
                          let templateVars = {};
                          try {
                            templateVars = typeof template.variables === 'string'
                              ? JSON.parse(template.variables)
                              : (template.variables || {});
                          } catch (e) {
                            console.error('Error parsing template variables:', e);
                          }

                          setNewsletterData(prev => ({
                            ...prev,
                            templateId: templateId,
                            subject: template.subject,
                            content: template.html_content,
                            templateVariables: templateVars
                          }));
                          setSelectedTemplate(template);
                        }
                      } catch (error) {
                        console.error('Error loading template:', error);
                        alert('Failed to load template');
                      }
                    }}
                    style={{
                      width: '100%',
                      padding: '8px',
                      borderRadius: '4px',
                      border: '1px solid var(--color-border)',
                      background: 'var(--color-input-bg)',
                      color: 'var(--color-text-primary)'
                    }}
                  >
                    <option value="">-- Select a Template --</option>
                    {emailTemplates.map(template => (
                      <option key={template.id} value={template.id}>{template.name}</option>
                    ))}
                  </select>
                </div>

                {/* Info message when template is selected */}
                {selectedTemplate && (
                  <div style={{ marginTop: '1rem', padding: '0.75rem', background: 'var(--color-info-bg)', borderRadius: '4px', border: '1px solid var(--color-info)' }}>
                    <p style={{ margin: 0, fontSize: '13px', color: 'var(--color-info)' }}>
                      ✓ Template loaded: <strong>{selectedTemplate.name}</strong>
                      <br />
                      <span style={{ fontSize: '12px', color: '#bbb' }}>
                        Template variables will be automatically filled from your selected articles, products, and tunes when you click "🤖 Generate AI Content".
                      </span>
                    </p>
                  </div>
                )}
              </div>
            )}
          </div>

          <button
            type="button"
            className="btn-primary"
            onClick={handleGenerateAIContent}
            disabled={isGenerating || newsletterData.linkedItems.length === 0}
            style={{
              width: '100%',
              padding: '12px',
              background: newsletterData.linkedItems.length === 0
                ? 'var(--color-disabled)'
                : 'linear-gradient(135deg, #4a90e2, #5b9ceb)',
              cursor: newsletterData.linkedItems.length === 0 ? 'not-allowed' : 'pointer'
            }}
          >
            {isGenerating ? '⏳ Generating...' : `🤖 Generate Content with AI ${newsletterData.linkedItems.length > 0 ? `(${newsletterData.linkedItems.length} items)` : '(No items selected)'}`}
          </button>
          {newsletterData.linkedItems.length === 0 && (
            <p style={{ fontSize: '12px', color: 'var(--color-warning)', marginTop: '8px', marginBottom: 0 }}>
              💡 Add articles, products, or tunes below first, then generate AI content.
            </p>
          )}

          {/* AI Creativity Slider */}
          <div style={{ marginTop: '20px', paddingTop: '20px', borderTop: '1px solid var(--color-border)' }}>
            <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px' }}>
              🎨 AI Creativity Level
            </label>
            <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
              <span style={{ fontSize: '12px', color: 'var(--color-text-secondary)', minWidth: '60px' }}>
                Factual
              </span>
              <input
                type="range"
                min="0"
                max="100"
                value={Math.round(newsletterData.aiTemperature * 50)}
                onChange={(e) => setNewsletterData({
                  ...newsletterData,
                  aiTemperature: parseInt(e.target.value) / 50
                })}
                style={{
                  flex: 1,
                  height: '6px',
                  borderRadius: '3px',
                  background: `linear-gradient(to right, 
                    #4a90e2 0%, 
                    #5b9ceb ${Math.round(newsletterData.aiTemperature * 50)}%, 
                    #444 ${Math.round(newsletterData.aiTemperature * 50)}%, 
                    #444 100%)`,
                  outline: 'none',
                  cursor: 'pointer'
                }}
              />
              <span style={{ fontSize: '12px', color: 'var(--color-text-secondary)', minWidth: '60px', textAlign: 'right' }}>
                Creative
              </span>
            </div>
            <div style={{
              marginTop: '8px',
              fontSize: '12px',
              color: 'var(--color-text-secondary)',
              textAlign: 'center'
            }}>
              {newsletterData.aiTemperature < 0.4 && '📊 Focus on facts and accuracy'}
              {newsletterData.aiTemperature >= 0.4 && newsletterData.aiTemperature < 0.8 && '✨ Balanced and engaging'}
              {newsletterData.aiTemperature >= 0.8 && newsletterData.aiTemperature < 1.2 && '🎨 Creative and expressive'}
              {newsletterData.aiTemperature >= 1.2 && '🚀 Very creative and imaginative'}
            </div>
          </div>
        </div>

        {/* Design & Customization */}
        <div className="newsletter-link-section" style={{
          background: 'var(--color-surface)',
          border: '1px solid var(--color-border)',
          borderRadius: '8px',
          padding: '20px',
          marginBottom: '20px'
        }}>
          <h4 style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: 0 }}>
            <span>🎨</span> Design & Customization
          </h4>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '20px' }}>
            {/* Theme Selection */}
            <div className="form-group" style={{ margin: 0 }}>
              <label htmlFor="backgroundImage" style={{ display: 'block', marginBottom: '8px' }}>Newsletter Color Theme</label>
              <select
                id="backgroundImage"
                name="backgroundImage"
                value={newsletterData.backgroundImage}
                onChange={handleInputChange}
                style={{
                  width: '100%',
                  padding: '10px',
                  borderRadius: '5px',
                  border: '1px solid var(--color-border)',
                  background: 'var(--color-input-bg)',
                  color: 'var(--color-text-primary)'
                }}
              >
                <optgroup label="Standard Themes">
                  <option value="">Default (Classic Blue)</option>
                  <option value="music">Celtic Music (Gray/Red)</option>
                  <option value="scotland">Scotland (Royal Blue)</option>
                </optgroup>
                <optgroup label="Seasonal Themes">
                  <option value="winter">❄️ Winter / Ice Theme</option>
                  <option value="spring">🌸 Spring / Nature Theme</option>
                  <option value="summer">☀️ Summer / Ocean Theme</option>
                  <option value="autumn">🍂 Autumn / Earth Theme</option>
                </optgroup>
                <optgroup label="Holiday Themes">
                  <option value="christmas">🎄 Christmas / Holiday Theme</option>
                  <option value="newyear">🎆 New Year / Dark Theme</option>
                </optgroup>
              </select>
              <p style={{ fontSize: '12px', color: 'var(--color-text-secondary)', marginTop: '5px' }}>
                This theme applies a cohesive color palette to the entire email (Header, Content Background, Footer).
              </p>
            </div>

            {/* Custom Background Image Selection */}
            <div className="form-group" style={{ margin: 0 }}>
              <label style={{ display: 'block', marginBottom: '8px' }}>Custom Background Image (Optional)</label>

              <div style={{ display: 'flex', gap: '10px', alignItems: 'flex-start' }}>
                <div style={{ flex: 1 }}>
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <input
                      type="text"
                      placeholder="Image URL..."
                      value={newsletterData.backgroundUrl || ''}
                      onChange={(e) => setNewsletterData({ ...newsletterData, backgroundUrl: e.target.value })}
                      style={{
                        flex: 1,
                        padding: '10px',
                        borderRadius: '5px',
                        border: '1px solid var(--color-border)',
                        background: 'var(--color-input-bg)',
                        color: 'var(--color-text-primary)'
                      }}
                    />
                    <button
                      type="button"
                      className="btn-secondary"
                      onClick={() => setShowAssetModal(true)}
                      style={{ padding: '0 15px' }}
                    >
                      Select Image
                    </button>
                  </div>
                  <p style={{ fontSize: '12px', color: 'var(--color-text-secondary)', marginTop: '5px' }}>
                    Select an image from assets or paste a URL. This will overlay the selected color theme.
                  </p>
                </div>

                {newsletterData.backgroundUrl && (
                  <div style={{
                    width: '60px',
                    height: '60px',
                    borderRadius: '4px',
                    overflow: 'hidden',
                    border: '1px solid var(--color-border)',
                    backgroundImage: `url(${newsletterData.backgroundUrl})`,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center'
                  }} title="Background Preview" />
                )}
              </div>
            </div>

          </div>
        </div>

        <AssetSearchModal
          isOpen={showAssetModal}
          onClose={() => setShowAssetModal(false)}
          onSelectAsset={(asset) => {
            const assetUrl = `/api/assets/${asset.id}/download`; // Or use asset.public_url if available
            setNewsletterData({ ...newsletterData, backgroundUrl: assetUrl });
          }}
          fileType="image"
        />

        {/* Basic Info */}
        <div className="form-group">
          <label htmlFor="title">Newsletter Title</label>
          <input
            type="text"
            id="title"
            name="title"
            value={newsletterData.title}
            onChange={handleInputChange}
            placeholder="Enter newsletter title..."
          />
        </div>

        <div className="form-group">
          <label htmlFor="subject">Email Subject</label>
          <input
            type="text"
            id="subject"
            name="subject"
            value={newsletterData.subject}
            onChange={handleInputChange}
            placeholder="Enter email subject line..."
          />
        </div>

        {/* Content */}
        <div className="form-group">
          <label htmlFor="content">Newsletter Content</label>
          <textarea
            id="content"
            name="content"
            value={newsletterData.content}
            onChange={handleInputChange}
            rows="10"
            placeholder="Write your newsletter content here, or use AI generation above..."
          />
        </div>

        {/* Tags */}
        <div className="form-group">
          <label>Tags</label>
          <div className="tags-container">
            {newsletterData.tags.map(tag => (
              <span key={tag} className="tag">
                {tag}
                <button type="button" onClick={() => handleTagRemove(tag)}>×</button>
              </span>
            ))}
            <input
              type="text"
              placeholder="Add tag..."
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  handleTagAdd(e.target.value);
                  e.target.value = '';
                }
              }}
            />
          </div>
        </div>

        {/* Scheduling */}
        <div className="form-group">
          <label className="checkbox-label">
            <input
              type="checkbox"
              name="scheduled"
              checked={newsletterData.scheduled}
              onChange={handleInputChange}
            />
            Schedule for later
          </label>
        </div>

        {newsletterData.scheduled && (
          <div className="form-group">
            <label htmlFor="scheduledDate">Scheduled Date</label>
            <input
              type="datetime-local"
              id="scheduledDate"
              name="scheduledDate"
              value={newsletterData.scheduledDate}
              onChange={handleInputChange}
            />
          </div>
        )}

        {/* Newsletter Preview */}
        <div className="newsletter-preview-section">
          <h4>Newsletter Preview</h4>
          <div className="newsletter-preview-cards">
            {newsletterData.linkedItems.map((item, idx) => (
              <div key={idx} className="newsletter-preview-card">
                {item.image && <img src={getProductImage(item)} alt={item.title} className="newsletter-preview-image" />}
                <div className="newsletter-preview-content">
                  <h5>{item.title}</h5>
                  {item.summary && <p>{item.summary}</p>}
                  {item.description && <p>{item.description}</p>}
                  <a href={item.url} target="_blank" rel="noopener noreferrer">Read more</a>
                  <span className="newsletter-preview-type">[{item.type}]</span>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Recipient Selection */}
        <div className="newsletter-link-section" style={{ marginTop: '30px' }}>
          <h4>Recipients</h4>

          {newsletterData.contentFormat === 'full_html' && (
            <div style={{
              marginBottom: '15px',
              padding: '12px 14px',
              borderRadius: '8px',
              background: 'rgba(59,130,246,0.12)',
              border: '1px solid rgba(59,130,246,0.35)',
              color: 'var(--color-text-primary)',
              fontSize: '0.92em'
            }}>
              This campaign uses the full HTML email template (not the standard newsletter wrapper).
            </div>
          )}

          <div style={{ marginBottom: '20px', background: 'var(--color-surface)', padding: '15px', borderRadius: '8px', display: 'flex', gap: '20px', flexWrap: 'wrap', alignItems: 'center' }}>
            <label style={{
              display: 'flex',
              alignItems: 'center',
              cursor: isTestSendMode ? 'not-allowed' : 'pointer',
              margin: 0,
              opacity: isTestSendMode ? 0.55 : 1
            }}>
              <input
                type="radio"
                name="recipientMode"
                value="all"
                checked={newsletterData.recipientMode === 'all'}
                disabled={isTestSendMode}
                onChange={() => setNewsletterData(prev => ({ ...prev, recipientMode: 'all' }))}
                style={{ marginRight: '8px' }}
              />
              <span>Send to All Active Subscribers{isTestSendMode ? ' (blocked locally)' : ''}</span>
            </label>

            <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer', margin: 0 }}>
              <input
                type="radio"
                name="recipientMode"
                value="selected"
                checked={newsletterData.recipientMode === 'selected'}
                onChange={() => setNewsletterData(prev => ({ ...prev, recipientMode: 'selected' }))}
                style={{ marginRight: '8px' }}
              />
              <span>
                Select Specific Recipients
                {isTestSendMode ? ` (max ${maxTestRecipients} for local testing)` : ''}
              </span>
            </label>
          </div>

          {!isTestSendMode && (
          <div style={{
            marginBottom: '20px',
            background: 'var(--color-surface)',
            padding: '15px',
            borderRadius: '8px',
            display: 'flex',
            gap: '16px',
            flexWrap: 'wrap',
            alignItems: 'center'
          }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', margin: 0 }}>
              <span style={{ fontWeight: 600 }}>Batch size</span>
              <select
                value={newsletterData.batchSize || 100}
                onChange={(e) => setNewsletterData(prev => ({ ...prev, batchSize: Number(e.target.value) }))}
                style={{
                  padding: '8px 12px',
                  borderRadius: '6px',
                  border: '1px solid var(--color-border)',
                  background: 'var(--color-input-bg)',
                  color: 'var(--color-text-primary)'
                }}
              >
                <option value={100}>100</option>
                <option value={200}>200</option>
                <option value={500}>500</option>
                <option value={1000}>1000</option>
              </select>
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', margin: 0, cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={newsletterData.excludeAlreadySent !== false}
                onChange={(e) => setNewsletterData(prev => ({ ...prev, excludeAlreadySent: e.target.checked }))}
              />
              <span>Skip customers who already received this campaign</span>
            </label>
          </div>
          )}

          {newsletterData.recipientMode === 'selected' && newsletterData.selectedRecipients.length > 0 && (
            <div style={{ marginBottom: '15px', padding: '10px', background: 'var(--color-surface)', borderRadius: '6px' }}>
              <strong style={{ color: 'var(--color-text-primary)' }}>
                {newsletterData.selectedRecipients.length} recipient{newsletterData.selectedRecipients.length !== 1 ? 's' : ''} selected
              </strong>
              <button
                type="button"
                onClick={() => setNewsletterData(prev => ({ ...prev, selectedRecipients: [] }))}
                style={{
                  marginLeft: '10px',
                  padding: '4px 8px',
                  fontSize: '12px',
                  background: 'var(--color-error)',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer'
                }}
              >
                Clear All
              </button>
            </div>
          )}

          {newsletterData.recipientMode === 'selected' && (
            <div>
              <div className="search-wrapper" style={{ marginBottom: '15px' }}>
                <input
                  type="text"
                  placeholder="Search recipients by email or name..."
                  value={recipientSearchTerm}
                  onChange={(e) => setRecipientSearchTerm(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '10px 15px',
                    border: '2px solid var(--color-border)',
                    borderRadius: '5px',
                    fontSize: '14px',
                    background: 'var(--color-input-bg))',
                    color: 'var(--color-text-primary)'
                  }}
                />
              </div>

              <div className="newsletter-link-list" style={{ maxHeight: '300px', overflowY: 'auto', border: '1px solid var(--color-border)', borderRadius: '5px', padding: '10px' }}>
                {filteredRecipients.length > 0 ? (
                  filteredRecipients.map(recipient => {
                    const isSelected = newsletterData.selectedRecipients.includes(recipient.id);
                    return (
                      <button
                        type="button"
                        key={recipient.id}
                        onClick={() => {
                          setNewsletterData(prev => {
                            if (isSelected) {
                              return {
                                ...prev,
                                selectedRecipients: prev.selectedRecipients.filter(id => id !== recipient.id)
                              };
                            }
                            if (isTestSendMode && prev.selectedRecipients.length >= maxTestRecipients) {
                              alert(`Local test mode: select at most ${maxTestRecipients} recipients.`);
                              return prev;
                            }
                            return {
                              ...prev,
                              selectedRecipients: [...prev.selectedRecipients, recipient.id]
                            };
                          });
                        }}
                        style={{
                          display: 'block',
                          width: '100%',
                          textAlign: 'left',
                          padding: '10px',
                          marginBottom: '5px',
                          border: `1px solid ${isSelected ? 'var(--color-success)' : 'var(--color-border)'}`,
                          borderRadius: '5px',
                          background: isSelected ? 'var(--color-success))' : 'var(--color-surface)',
                          color: 'var(--color-text-primary)',
                          cursor: 'pointer',
                          transition: 'background 0.2s'
                        }}
                        onMouseEnter={(e) => {
                          if (!isSelected) {
                            e.currentTarget.style.background = 'var(--color-surface-hover)';
                          }
                        }}
                        onMouseLeave={(e) => {
                          if (!isSelected) {
                            const computedStyle = getComputedStyle(document.documentElement);
                            const surfaceColor = computedStyle.getPropertyValue('--color-surface').trim() || '#23272a';
                            e.currentTarget.style.background = surfaceColor;
                          }
                        }}
                      >
                        <div style={{ fontWeight: 'bold', marginBottom: '5px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <span>{recipient.name || recipient.email}</span>
                          {isSelected && <span style={{ fontSize: '12px', color: 'var(--color-success)' }}>✓ Selected</span>}
                        </div>
                        <div style={{ fontSize: '12px', color: 'var(--color-text-secondary)' }}>
                          {recipient.email}
                        </div>
                        <div style={{ fontSize: '11px', color: 'var(--color-text-secondary)', marginTop: '3px' }}>
                          Type: {recipient.type} | Status: {recipient.status}
                        </div>
                      </button>
                    );
                  })
                ) : (
                  <div style={{ padding: '20px', textAlign: 'center', color: 'var(--color-text-secondary)' }}>
                    {recipientSearchTerm ? 'No recipients found matching your search.' : 'No recipients available.'}
                  </div>
                )}
              </div>

              {newsletterData.selectedRecipients.length === 0 && (
                <div style={{ marginTop: '10px', padding: '10px', background: 'var(--color-warning))', borderRadius: '5px', color: 'var(--color-text-primary)' }}>
                  <strong>Note:</strong> If no recipients are selected, the newsletter will be sent to all active subscribers.
                </div>
              )}
            </div>
          )}
        </div>

        <div className="form-actions">
          <button type="button" className="btn-secondary" onClick={handlePreviewNewsletter}>
            📧 Preview Newsletter
          </button>
          <button type="button" className="btn-secondary" onClick={handleSaveDraft}>
            💾 Save Draft
          </button>
          <button
            type="button"
            className="btn-primary"
            onClick={handleSendNewsletter}
            disabled={isSending || !canSendTestSelection}
            title={
              isTestSendMode && !canSendTestSelection
                ? `Select 1–${maxTestRecipients} recipients for a local test send`
                : undefined
            }
          >
            {isSending
              ? 'Sending...'
              : (isTestSendMode
                ? `🧪 Send Test (${newsletterData.selectedRecipients.length}/${maxTestRecipients})`
                : (newsletterData.scheduled ? '📅 Schedule Newsletter' : '🚀 Send Newsletter'))}
          </button>
        </div>
      </form>
    </div>
  );

  const renderSubscribers = () => (
    <div className="subscribers-section">
      <h3>Subscribers Management</h3>

      <div className="subscribers-controls">
        <input
          type="text"
          placeholder="Search subscribers..."
          className="search-input"
        />
        <button className="btn-primary">Export List</button>
      </div>

      <div className="subscribers-table">
        {loadingSubscribers ? (
          <p>Loading subscribers...</p>
        ) : (
          <table>
            <thead>
              <tr>
                <th>Email</th>
                <th>Name</th>
                <th>Type</th>
                <th>Status</th>
                <th>Joined</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {subscribersList.length > 0 ? (
                subscribersList.map(subscriber => (
                  <tr key={subscriber.id}>
                    <td>{subscriber.email}</td>
                    <td>{subscriber.first_name} {subscriber.last_name}</td>
                    <td>
                      <span className={`badge ${subscriber.source === 'user' ? 'badge-primary' : 'badge-secondary'}`}>
                        {subscriber.source === 'user' ? 'User' : 'Subscriber'}
                      </span>
                    </td>
                    <td>
                      <span className={`status ${subscriber.status}`}>
                        {subscriber.status}
                      </span>
                    </td>
                    <td>{new Date(subscriber.created_at).toLocaleDateString()}</td>
                    <td>
                      <button
                        className="btn-small"
                        onClick={() => setEditingSubscriber(subscriber)}
                      >
                        Edit
                      </button>
                      <button
                        className="btn-small btn-danger"
                        onClick={() => handleRemoveSubscriber(subscriber.id)}
                      >
                        Remove
                      </button>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="6" style={{ textAlign: 'center' }}>No subscribers found.</td>
                </tr>
              )}
            </tbody>
          </table>
        )}
      </div>

      {/* Pagination */}
      {subscribersPagination.pages > 1 && (
        <div className="pagination">
          <button
            disabled={subscribersPagination.page === 1}
            onClick={() => fetchSubscribers(subscribersPagination.page - 1)}
          >
            Previous
          </button>
          <span>Page {subscribersPagination.page} of {subscribersPagination.pages}</span>
          <button
            disabled={subscribersPagination.page === subscribersPagination.pages}
            onClick={() => fetchSubscribers(subscribersPagination.page + 1)}
          >
            Next
          </button>
        </div>
      )}

      {/* Edit Subscriber Modal */}
      {editingSubscriber && (
        <div className="modal-overlay" style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.7)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 1000
        }}>
          <div className="modal-content" style={{
            background: 'var(--color-surface)', padding: '20px', borderRadius: '8px', width: '90%', maxWidth: '500px',
            border: '1px solid var(--color-border)'
          }}>
            <h3 style={{ marginTop: 0, color: 'var(--color-text-primary)' }}>Edit Subscriber</h3>
            <form onSubmit={handleUpdateSubscriber}>
              <div className="form-group">
                <label>Email</label>
                <input
                  type="email"
                  value={editingSubscriber.email}
                  onChange={e => setEditingSubscriber({ ...editingSubscriber, email: e.target.value })}
                  disabled={editingSubscriber.source === 'user'} // Users manage their own email
                  required
                />
                {editingSubscriber.source === 'user' && <small style={{ color: '#888' }}>User emails cannot be edited here.</small>}
              </div>
              <div className="form-group">
                <label>First Name</label>
                <input
                  type="text"
                  value={editingSubscriber.first_name || ''}
                  onChange={e => setEditingSubscriber({ ...editingSubscriber, first_name: e.target.value })}
                  disabled={editingSubscriber.source === 'user'}
                />
              </div>
              <div className="form-group">
                <label>Last Name</label>
                <input
                  type="text"
                  value={editingSubscriber.last_name || ''}
                  onChange={e => setEditingSubscriber({ ...editingSubscriber, last_name: e.target.value })}
                  disabled={editingSubscriber.source === 'user'}
                />
              </div>
              <div className="form-group">
                <label>Status</label>
                <select
                  value={editingSubscriber.status}
                  onChange={e => setEditingSubscriber({ ...editingSubscriber, status: e.target.value })}
                >
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                  <option value="unsubscribed">Unsubscribed</option>
                </select>
              </div>
              <div className="form-actions" style={{ justifyContent: 'flex-end', marginTop: '20px' }}>
                <button type="button" className="btn-secondary" onClick={() => setEditingSubscriber(null)}>Cancel</button>
                <button type="submit" className="btn-primary">Save Changes</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );

  const renderUserInfo = () => (
    <div className="user-info-section">
      <h3>User Information</h3>
      <div className="user-details">
        <p><strong>Username:</strong> {currentUser.username}</p>
        <p><strong>Role:</strong> {currentUser.roles?.map(role => role.name).join(', ')}</p>
        <p><strong>Level:</strong> {currentUser.level}</p>
        <p><strong>Name:</strong> {currentUser.profile?.first_name} {currentUser.profile?.last_name}</p>
        {currentUser.profile?.company_name && (
          <p><strong>Company:</strong> {currentUser.profile.company_name}</p>
        )}
      </div>

      <div className="permissions-list">
        <h4>Your Permissions</h4>
        <div className="permissions-grid">
          {currentUser.permissions?.map(permission => (
            <span key={permission} className="permission-badge">
              {permission}
            </span>
          ))}
        </div>
      </div>
    </div>
  );

  // Render automated newsletter section
  const renderAutomatedNewsletter = () => (
    <div className="automated-newsletter-section">
      <h3>🤖 Automated Newsletter Generator</h3>
      <p className="section-description">
        Generate engaging newsletters automatically with AI-powered content, featuring recent articles,
        new products, and promotions. Perfect for regular monthly or weekly newsletters!
      </p>

      <div className="automation-config">
        <h4>Content Selection</h4>

        <div className="form-group">
          <label className="checkbox-label">
            <input
              type="checkbox"
              name="includeRecentArticles"
              checked={automationConfig.includeRecentArticles}
              onChange={handleAutomationConfigChange}
            />
            Include Recent Articles
          </label>
          {automationConfig.includeRecentArticles && (
            <div className="sub-config">
              <label>
                Number of articles:
                <input
                  type="number"
                  name="articleLimit"
                  value={automationConfig.articleLimit}
                  onChange={handleAutomationConfigChange}
                  min="1"
                  max="10"
                  style={{ width: '60px', marginLeft: '10px' }}
                />
              </label>
              <label style={{ marginLeft: '20px' }}>
                Days back:
                <input
                  type="number"
                  name="daysBack"
                  value={automationConfig.daysBack}
                  onChange={handleAutomationConfigChange}
                  min="1"
                  max="365"
                  style={{ width: '60px', marginLeft: '10px' }}
                />
              </label>
            </div>
          )}
        </div>

        <div className="form-group">
          <label className="checkbox-label">
            <input
              type="checkbox"
              name="includeNewProducts"
              checked={automationConfig.includeNewProducts}
              onChange={handleAutomationConfigChange}
            />
            Include New Products
          </label>
          {automationConfig.includeNewProducts && (
            <div className="sub-config">
              <label>
                Number of products:
                <input
                  type="number"
                  name="productLimit"
                  value={automationConfig.productLimit}
                  onChange={handleAutomationConfigChange}
                  min="1"
                  max="10"
                  style={{ width: '60px', marginLeft: '10px' }}
                />
              </label>
            </div>
          )}
        </div>

        <div className="form-group">
          <label className="checkbox-label">
            <input
              type="checkbox"
              name="includePromotions"
              checked={automationConfig.includePromotions}
              onChange={handleAutomationConfigChange}
            />
            Include Promotions (if available)
          </label>
        </div>

        <h4>Content Style</h4>
        <div className="form-group">
          <label>
            Theme:
            <input
              type="text"
              name="theme"
              value={automationConfig.theme}
              onChange={handleAutomationConfigChange}
              placeholder="e.g., celtic music, fiddle tunes"
              style={{ width: '100%', marginTop: '5px' }}
            />
          </label>
        </div>

        <div className="form-group">
          <label>
            Tone:
            <select
              name="tone"
              value={automationConfig.tone}
              onChange={handleAutomationConfigChange}
              style={{ width: '100%', marginTop: '5px' }}
            >
              <option value="friendly and engaging">Friendly and Engaging</option>
              <option value="professional">Professional</option>
              <option value="casual">Casual</option>
              <option value="enthusiastic">Enthusiastic</option>
              <option value="informative">Informative</option>
            </select>
          </label>
        </div>

        <h4>AI Provider</h4>
        <div className="form-group">
          <label>
            Select AI Provider:
            <select
              name="aiProvider"
              value={automationConfig.aiProvider}
              onChange={handleAutomationConfigChange}
              style={{ width: '100%', marginTop: '5px' }}
            >
              <option value="watsonx">IBM Watsonx (Llama 3.3) - Recommended</option>
              <option value="openai">OpenAI GPT-4</option>
              <option value="template">Template-Based (No AI)</option>
            </select>
          </label>
          <p style={{ fontSize: '0.85rem', color: '#8b949e', marginTop: '5px' }}>
            {automationConfig.aiProvider === 'watsonx' && 'Using IBM Watsonx with Llama 3.3 70B model'}
            {automationConfig.aiProvider === 'openai' && 'Using OpenAI GPT-4 (requires OPENAI_API_KEY)'}
            {automationConfig.aiProvider === 'template' && 'Using template-based generation (no AI)'}
          </p>
        </div>

        <h4>Schedule (Optional)</h4>
        <div className="form-group">
          <label>
            Schedule Date (leave empty for immediate):
            <input
              type="datetime-local"
              name="scheduleDate"
              value={automationConfig.scheduleDate}
              onChange={handleAutomationConfigChange}
              style={{ width: '100%', marginTop: '5px' }}
            />
          </label>
        </div>

        <div className="form-actions" style={{ marginTop: '20px' }}>
          <button
            type="button"
            className="btn-secondary"
            onClick={handlePreviewAutomated}
            disabled={isPreviewing}
          >
            {isPreviewing ? 'Generating Preview...' : 'Preview Newsletter'}
          </button>
          <button
            type="button"
            className="btn-primary"
            onClick={handleGenerateAutomated}
            disabled={isGenerating}
          >
            {isGenerating ? 'Generating...' : 'Generate & Create Campaign'}
          </button>
        </div>

        {automatedPreview && (
          <div className="preview-section" style={{ marginTop: '30px', padding: '20px', borderRadius: '8px' }}>
            <h4>Preview</h4>
            <div style={{ marginBottom: '15px' }}>
              <strong>Subject:</strong> {automatedPreview.subject}
            </div>
            <div style={{ marginBottom: '15px' }}>
              <strong>Content Summary:</strong>
              <ul>
                <li>Articles: {automatedPreview.metadata?.articlesCount || 0}</li>
                <li>Products: {automatedPreview.metadata?.productsCount || 0}</li>
                <li>Promotions: {automatedPreview.metadata?.promotionsCount || 0}</li>
              </ul>
            </div>
            <div style={{ maxHeight: '400px', overflow: 'auto', border: '2px solid var(--color-border)', padding: '15px', background: 'white' }}>
              <div dangerouslySetInnerHTML={{ __html: automatedPreview.htmlContent }} />
            </div>
          </div>
        )}
      </div>
    </div>
  );

  // Determine available tabs based on user permissions
  const availableTabs = [];

  if (hasPermission('newsletter.view') || hasPermission('*')) {
    availableTabs.push({ id: 'overview', label: 'Overview' });
  }

  if (hasPermission('newsletter.create') || hasPermission('*')) {
    availableTabs.push({ id: 'create', label: '📧 Create Email' });
  }

  if (hasPermission('newsletter.manage_subscribers') || hasPermission('*')) {
    availableTabs.push({ id: 'subscribers', label: 'Subscribers' });
  }

  // Everyone can see their user info
  availableTabs.push({ id: 'profile', label: 'Profile' });

  return (
    <div className="newsletter-dashboard">
      <div className="dashboard-header">
        <h2>Newsletter Dashboard</h2>
        <div className="user-role-badge">
          <span className={`role-badge ${currentUser.roles?.[0]?.name}`}>
            {currentUser.roles?.[0]?.name?.replace('_', ' ').toUpperCase()}
          </span>
        </div>
      </div>

      {isTestSendMode && (
        <div
          role="status"
          style={{
            margin: '0 0 16px',
            padding: '14px 16px',
            borderRadius: '8px',
            border: '1px solid #b45309',
            background: 'rgba(245, 158, 11, 0.15)',
            color: 'var(--color-text-primary)'
          }}
        >
          <strong>🧪 Local test mode — mass sends blocked</strong>
          <div style={{ marginTop: '6px', fontSize: '0.92em', lineHeight: 1.45 }}>
            You can send real email to <strong>1–{maxTestRecipients}</strong> selected people only.
            “All subscribers” and batch sends (100/200/500/1000) are disabled here.
            Use Create Email → Specific Recipients → pick yourself (and maybe one other) → Send Test.
          </div>
        </div>
      )}

      <div className="dashboard-tabs">
        {availableTabs.map(tab => (
          <button
            key={tab.id}
            className={`tab-button ${activeTab === tab.id ? 'active' : ''}`}
            onClick={() => setActiveTab(tab.id)}
          >
            {tab.label}
          </button>
        ))}
      </div>

      <div className="dashboard-content">
        {activeTab === 'overview' && renderOverview()}
        {activeTab === 'create' && renderUnifiedEmailCreator()}
        {activeTab === 'subscribers' && renderSubscribers()}
        {activeTab === 'profile' && renderUserInfo()}
      </div>

      {/* Custom Delete Confirmation Modal */}
      {deleteConfirm && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.7)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 10000
        }}>
          <div style={{
            background: 'var(--color-surface)',
            padding: '30px',
            borderRadius: '8px',
            maxWidth: '400px',
            boxShadow: '0 4px 6px rgba(0, 0, 0, 0.3)'
          }}>
            <h3 style={{ marginTop: 0, color: 'var(--color-text-primary)' }}>Confirm Delete</h3>
            <p style={{ color: 'var(--color-text-primary)' }}>
              {deleteConfirm.type === 'bulk' ? (
                <>
                  Are you sure you want to delete <strong>{deleteConfirm.count}</strong> campaigns?
                </>
              ) : (
                <>
                  Are you sure you want to delete the campaign<br />
                  <strong>"{deleteConfirm.title}"</strong>?
                </>
              )}
            </p>
            <p style={{ color: 'var(--color-error)', fontSize: '14px' }}>
              This action cannot be undone.
            </p>
            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end', marginTop: '20px' }}>
              <button
                onClick={cancelDelete}
                className="btn-secondary"
                style={{ padding: '10px 20px' }}
              >
                Cancel
              </button>
              <button
                onClick={confirmDelete}
                className="btn-primary"
                style={{ padding: '10px 20px', background: 'var(--color-error)' }}
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Custom Send Success Modal */}
      {sendSuccessModal && sendSuccessModal.visible && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.7)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 10000
        }}>
          <div style={{
            background: 'var(--color-surface)',
            padding: '30px',
            borderRadius: '8px',
            maxWidth: '500px',
            boxShadow: '0 4px 6px rgba(0, 0, 0, 0.3)',
            textAlign: 'center'
          }}>
            <div style={{ fontSize: '48px', marginBottom: '15px' }}>✅</div>
            <h3 style={{ marginTop: 0, color: 'var(--color-text-primary)' }}>Newsletter Sent!</h3>
            <p style={{ color: 'var(--color-text-primary)', marginBottom: '10px' }}>
              Campaign ID: <strong>{sendSuccessModal.campaignId}</strong>
            </p>
            <p style={{ color: 'var(--color-text-primary)' }}>
              The newsletter is being sent to<br />
              <strong>{sendSuccessModal.recipientInfo}</strong>.
            </p>
            <div style={{ display: 'flex', gap: '10px', justifyContent: 'center', marginTop: '25px' }}>
              <button
                onClick={() => {
                  setSendSuccessModal(null);
                  setActiveTab('overview');
                }}
                className="btn-secondary"
                style={{ padding: '10px 20px' }}
              >
                View in Overview
              </button>
              <button
                onClick={() => setSendSuccessModal(null)}
                className="btn-primary"
                style={{ padding: '10px 20px', background: '#4caf50' }}
              >
                Continue Editing
              </button>
            </div>
          </div>
        </div>
      )}

      {batchSendModal && (
        <div style={{
          position: 'fixed',
          inset: 0,
          background: 'rgba(0, 0, 0, 0.7)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 10000
        }}>
          <div style={{
            background: 'var(--color-surface)',
            padding: '28px',
            borderRadius: '8px',
            maxWidth: '440px',
            width: '92%',
            boxShadow: '0 4px 6px rgba(0, 0, 0, 0.3)'
          }}>
            <h3 style={{ marginTop: 0 }}>Send Campaign Batch</h3>
            <p style={{ color: 'var(--color-text-secondary)' }}>
              #{batchSendModal.campaignId} — {batchSendModal.title}
            </p>
            {typeof batchSendModal.remaining === 'number' && (
              <p>Estimated remaining before this batch: <strong>{batchSendModal.remaining}</strong></p>
            )}
            <label style={{ display: 'block', marginBottom: '16px' }}>
              <span style={{ display: 'block', marginBottom: '6px', fontWeight: 600 }}>Batch size</span>
              <select
                value={batchSendModal.batchSize}
                onChange={(e) => setBatchSendModal((prev) => ({ ...prev, batchSize: Number(e.target.value) }))}
                style={{
                  width: '100%',
                  padding: '10px',
                  borderRadius: '6px',
                  border: '1px solid var(--color-border)',
                  background: 'var(--color-input-bg)',
                  color: 'var(--color-text-primary)'
                }}
              >
                <option value={100}>100</option>
                <option value={200}>200</option>
                <option value={500}>500</option>
                <option value={1000}>1000</option>
              </select>
            </label>
            <p style={{ fontSize: '0.9em', color: 'var(--color-text-secondary)' }}>
              Customers who already received this campaign are skipped automatically.
            </p>
            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end', marginTop: '18px' }}>
              <button type="button" className="btn-secondary" onClick={() => setBatchSendModal(null)}>
                Cancel
              </button>
              <button
                type="button"
                className="btn-primary"
                disabled={isSending || massSendsBlocked}
                onClick={() => handleSendCampaignBatch(batchSendModal.campaignId, batchSendModal.batchSize)}
              >
                {massSendsBlocked ? '🚫 Batch Blocked' : (isSending ? 'Starting…' : `Send ${batchSendModal.batchSize}`)}
              </button>
            </div>
          </div>
        </div>
      )}

      {campaignRecipientsModal && (
        <div style={{
          position: 'fixed',
          inset: 0,
          background: 'rgba(0, 0, 0, 0.7)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 10000
        }}>
          <div style={{
            background: 'var(--color-surface)',
            padding: '24px',
            borderRadius: '8px',
            maxWidth: '720px',
            width: '94%',
            maxHeight: '85vh',
            overflow: 'auto',
            boxShadow: '0 4px 6px rgba(0, 0, 0, 0.3)'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: '12px', alignItems: 'flex-start' }}>
              <div>
                <h3 style={{ marginTop: 0, marginBottom: '6px' }}>
                  Campaign #{campaignRecipientsModal.campaignId} — Who Received
                </h3>
                <p style={{ margin: 0, color: 'var(--color-text-secondary)' }}>
                  {campaignRecipientsModal.name}
                </p>
                <p style={{ marginTop: '8px' }}>
                  Received: <strong>{campaignRecipientsModal.alreadySent}</strong>
                  {' · '}
                  Remaining: <strong>{campaignRecipientsModal.remaining}</strong>
                  {' · '}
                  Audience: <strong>{campaignRecipientsModal.totalAudience}</strong>
                </p>
              </div>
              <button type="button" className="btn-secondary" onClick={() => setCampaignRecipientsModal(null)}>
                Close
              </button>
            </div>
            <div style={{ marginTop: '16px', overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.9em' }}>
                <thead>
                  <tr>
                    <th style={{ textAlign: 'left', padding: '8px', borderBottom: '1px solid var(--color-border)' }}>Email</th>
                    <th style={{ textAlign: 'left', padding: '8px', borderBottom: '1px solid var(--color-border)' }}>Name</th>
                    <th style={{ textAlign: 'left', padding: '8px', borderBottom: '1px solid var(--color-border)' }}>Received</th>
                    <th style={{ textAlign: 'left', padding: '8px', borderBottom: '1px solid var(--color-border)' }}>Sent at</th>
                  </tr>
                </thead>
                <tbody>
                  {(campaignRecipientsModal.recipients || []).map((r) => (
                    <tr key={r.email}>
                      <td style={{ padding: '8px', borderBottom: '1px solid var(--color-border)' }}>{r.email}</td>
                      <td style={{ padding: '8px', borderBottom: '1px solid var(--color-border)' }}>{r.name}</td>
                      <td style={{ padding: '8px', borderBottom: '1px solid var(--color-border)' }}>
                        {r.received ? '✅ Yes' : '⏳ No'}
                      </td>
                      <td style={{ padding: '8px', borderBottom: '1px solid var(--color-border)' }}>
                        {r.sentAt ? new Date(r.sentAt).toLocaleString() : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default NewsletterDashboard; 